import { useState, useCallback, useEffect } from "react";
import html2pdf from "html2pdf.js";
import "@/App.css";

// ==================== CONSTANTS ====================
const BLIND_TYPES = {
  ezyroll: "EZY Roll",
  ezychannel: "EZY Channel",
  ezytrack: "EZY Track"
};

const DEDUCTIONS = {
  ezyroll: { amount: 65, label: "Hood deduction: -65 mm" },
  ezychannel: { amount: 65, label: "Hood deduction: -65 mm" },
  ezytrack: { amount: 70, label: "Hood deduction: -70 mm" }
};

// ==================== CALCULATION FORMULAS ====================

// Hood size calculation
const getHoodSize = (width, blindType) => {
  return width;
};

// Tube diameter calculation
const getTubeDiameter = (width, blindType) => {
  if (blindType === 'ezyroll') {
    return width < 4000 ? 63 : 75; // 63mm under 4m, 75mm over 4m
  }
  // Placeholder for other blind types
  if (width <= 2000) return 57;
  if (width <= 3000) return 65;
  return 78;
};

// Tube length calculation - needs operation and gearbox spacer info
const getTubeLength = (width, blindType, operation, gearboxSpacer) => {
  if (blindType === 'ezyroll') {
    const hoodSize = width; // Hood is same as width for EZY Roll
    
    if (gearboxSpacer) {
      return width - 65; // Gearbox spacer: deduct 65mm
    }
    
    switch (operation) {
      case 'Crank':
        return width - 60; // Crank: deduct 60mm
      case 'Motor':
        return hoodSize - 58; // Motor: deduct 58mm from hood size
      case 'Spring':
        return width - 56; // Spring: deduct 56mm
      default:
        return width - 60;
    }
  }
  // Placeholder for other blind types
  return width - 50;
};

// Bottom bar size calculation
const getBottomBarSize = (width, blindType) => {
  if (blindType === 'ezyroll') {
    return width - 80; // Deduct 80mm for all operations
  }
  // Placeholder for other blind types
  return width - 30;
};

// Get bottom bar/tube info for display
const getBottomBarInfo = (width, blindType) => {
  if (blindType === 'ezyroll') {
    return {
      label: "Bottom Tube",
      size: width - 80,
      diameter: 50,
      note: "50mm diameter tube"
    };
  }
  return {
    label: "Bottom Bar",
    size: width - 30,
    diameter: null,
    note: ""
  };
};

// Get tube length note based on operation
const getTubeLengthNote = (operation, gearboxSpacer, reverseGearbox) => {
  let note = "";
  
  if (gearboxSpacer) {
    note = "Gearbox Spacer (-65mm)";
  } else {
    switch (operation) {
      case 'Crank': note = "Crank operation (-60mm)"; break;
      case 'Motor': note = "Motorised (-58mm)"; break;
      case 'Spring': note = "Spring operation (-56mm)"; break;
      case 'Remote': note = "Motorised/Remote (-58mm)"; break;
      default: note = "";
    }
  }
  
  // Add reversed gearbox if applicable
  if (reverseGearbox && note) {
    note = note + " with Reversed Gear Box";
  } else if (reverseGearbox) {
    note = "Reversed Gear Box";
  }
  
  return note;
};

const FABRIC_OPTIONS = ["Clear", "Tint", "Visiontex Plus", "Visiontex Ultra"];

// HVG VisionTex Plus colour range
const VISIONTEX_PLUS_COLOURS = [
  "Amazon", "Arizona", "Baltic Blue", "Bondi", "Carbon Black", "Desert",
  "Dusk Grey", "Everest", "Evergreen", "Graphite", "Greystone", "Havana",
  "Ice White", "Ivory", "Jamaica", "Java", "Kashmir", "Kelp", "Neptune",
  "Oyster", "Paperbark", "Platinum", "Titan Grey"
];

// HVG VisionTex Ultra colour range
const VISIONTEX_ULTRA_COLOURS = [
  "Basalt", "Bluestone", "Claystone", "Goldstone", "Granite", "Limestone",
  "Onyx", "Pebble", "Riverstone", "Sahara", "Sandstone", "Shale", "Tungsten"
];

const OPERATION_OPTIONS = ["Spring", "Crank", "Motor", "Remote"];
const OPERATION_SIDE_OPTIONS = ["Left", "Right"];
const LOCK_HANDLES_OPTIONS = ["None", "Inside Only", "Outside Only", "Both Sides"];
const VALANCE_OPTIONS = ["Scalloped", "Straight", "Not Required"];
const TRIM_COLOUR_OPTIONS = ["None", "Black", "White", "Primrose", "Grey", "Paperbark"];
const APPLICATION_OPTIONS = ["Recess Fit", "Face Fit", "Internal", "External"];

// ==================== HOW-TO GUIDE (PRINTABLE) ====================
function generateHowToGuideHTML() {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>How to Use — Blind Measurement Worksheet</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Inter', Arial, sans-serif; color: #222; margin: 0; padding: 8mm; background: #fff; line-height: 1.55; font-size: 12px; }
  h1 { font-family: 'Bebas Neue', sans-serif; font-size: 30px; letter-spacing: 3px; color: #0a2540; margin: 0 0 4px; }
  h1 .accent { color: #1e5aa8; }
  h2 { font-family: 'Bebas Neue', sans-serif; font-size: 18px; letter-spacing: 2px; color: #0a2540; margin: 20px 0 8px; padding: 6px 12px; background: #eef3fb; border-left: 4px solid #1e5aa8; page-break-after: avoid; }
  h3 { font-family: 'Bebas Neue', sans-serif; font-size: 14px; letter-spacing: 1.5px; color: #6d4c00; margin: 14px 0 4px; page-break-after: avoid; }
  p { margin: 4px 0 8px; }
  ul, ol { margin: 4px 0 10px 20px; padding: 0; }
  li { margin: 3px 0; }
  .header {
    display: flex; align-items: center; justify-content: space-between;
    border-bottom: 3px solid #0a2540; padding-bottom: 10px; margin-bottom: 12px;
    page-break-after: avoid;
  }
  .subhead { color: #6b7f99; font-size: 12px; margin-top: 4px; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; margin-right: 4px; }
  .badge-black { background: #0a2540; color: #fff; }
  .badge-yellow { background: #f9a825; color: #222; }
  .badge-red { background: #c62828; color: #fff; }
  .badge-blue { background: #283593; color: #fff; }
  .card { border: 1.5px solid #d4dbe8; border-radius: 8px; padding: 10px 14px; margin: 8px 0; background: #fafcff; page-break-inside: avoid; }
  .card-yellow { border-color: #f9a825; background: #fffde7; }
  .card-red { border-color: #c62828; background: #ffebee; }
  .card-blue { border-color: #283593; background: #e8eaf6; }
  .card-black { border-color: #333; background: #f5f5f5; }
  .card h3 { margin-top: 0; }
  .callout { background: #fff9c4; border-left: 4px solid #f9a825; padding: 8px 12px; margin: 8px 0; border-radius: 4px; font-size: 12px; }
  .step-num { display: inline-block; width: 24px; height: 24px; line-height: 24px; text-align: center; background: #1e5aa8; color: #fff; border-radius: 50%; font-weight: 700; margin-right: 8px; font-size: 12px; }
  table.fields { width: 100%; border-collapse: collapse; margin: 6px 0 12px; font-size: 11px; }
  table.fields th, table.fields td { border: 1px solid #d4dbe8; padding: 6px 10px; text-align: left; vertical-align: top; }
  table.fields th { background: #eef3fb; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; color: #0a2540; width: 26%; }
  code { background: #f0f4fa; padding: 1px 6px; border-radius: 3px; font-family: 'SF Mono', Consolas, monospace; font-size: 11px; color: #b71c1c; }
  .footer { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9px; color: #aaa; letter-spacing: 1.5px; text-align: center; }
  .avoid-break { page-break-inside: avoid; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <h1>HOW TO USE <span class="accent">THE BLIND WORKSHEET APP</span></h1>
      <div class="subhead">The Outdoor Blind Co — internal reference sheet · Print &amp; keep by the workbench</div>
    </div>
  </div>

  <h2>1. What is this app?</h2>
  <p>A private in-house web tool that turns one set of blind measurements into all the paperwork the factory needs. Enter each blind once and the app produces four (or five, with powder coating) colour-coded worksheets, does every deduction automatically, and can save PDFs to a shared cloud folder anyone in the business can open from any device.</p>

  <h2>2. What is it for?</h2>
  <ul>
    <li><strong>One entry, all the sheets.</strong> No more copying numbers between forms.</li>
    <li><strong>Standardises the maths.</strong> All hood, tube, cassette, channel, bottom-bar and skin deductions are built in.</li>
    <li><strong>Splits jobs the right way.</strong> Manufacturing gets skin sizes only. Back Factory gets hardware sizes only. Office keeps the full picture.</li>
    <li><strong>Aggregates ordering.</strong> Order forms total hardware across the whole job in 6 m lengths — reception just orders totals.</li>
    <li><strong>Never lose a worksheet.</strong> Save-to-Cloud puts every PDF in a shared folder visible on every device.</li>
  </ul>

  <h2>3. The main screen — what each part does</h2>

  <div class="card avoid-break">
    <h3>Job Details (top of page)</h3>
    <table class="fields">
      <tr><th>Client Name</th><td>Customer's name — appears on every printed sheet.</td></tr>
      <tr><th>Quote No.</th><td>Your Xero or internal quote reference (e.g. <code>Q-0042</code>).</td></tr>
      <tr><th>Date</th><td>Defaults to today. Change it if you're back-filling an old job.</td></tr>
    </table>
  </div>

  <div class="card avoid-break">
    <h3>Blind Cards — one per blind</h3>
    <p>Click <strong>+ Add Blind</strong> to add more blinds. Each card can be collapsed by clicking its blue header. Fields on each card:</p>
    <table class="fields">
      <tr><th>Blind Name / Location</th><td>Free text — e.g. <code>Alfresco left</code>, <code>Living room</code>.</td></tr>
      <tr><th>Blind Type</th><td>EZY Roll, EZY Channel, or EZY Track. Deductions change automatically.</td></tr>
      <tr><th>Width / Drop</th><td>EZY Roll &amp; EZY Channel use a single Width + Drop.<br/>EZY Track uses <strong>Hood Size</strong> (drives Back Factory hardware) + <strong>Blind Size</strong> (drives Manufacturing skin) + Drop.</td></tr>
      <tr><th>Fabric</th><td>Clear, Tint, Visiontex Plus, Visiontex Ultra. Picking VT Plus or Ultra shows a dropdown of the HVG colour range.</td></tr>
      <tr><th>Operation</th><td>Spring / Crank / Motor / Remote — plus Operation Side (Left / Right).</td></tr>
      <tr><th>Application</th><td>Recess Fit / Face Fit / Internal / External.</td></tr>
      <tr><th>Lock Handles</th><td>None / Inside Only / Outside Only / Both Sides.</td></tr>
      <tr><th>Colours</th><td>Hood, Channel, Bottom Bar, Trim colours — printed for Back Factory reference.</td></tr>
      <tr><th>Weather Flaps, Backing Plate, Reverse Gearbox, Gearbox Spacer</th><td>Tick as required — they appear on the checklists automatically.</td></tr>
      <tr><th>Powder Coating</th><td>Tick to enable, then fill in the powder-coat <strong>Colour</strong> and which <strong>Hardware</strong> is being coated (e.g. <code>Hood, channels</code> or just <code>All</code>).</td></tr>
      <tr><th>Central Locking (EZY Track only)</th><td>Tick to calculate 2 flat bars: <code>(B/Bar ÷ 2) − 113 mm</code> and <code>(B/Bar ÷ 2) − 40 mm</code>.</td></tr>
      <tr><th>Notes</th><td>Free text. Two magic prefixes create checklist items automatically:<br/>
        · <code>Manufacturing: item1, item2</code> → items appear on the Manufacturing sheet as tick-boxes.<br/>
        · <code>Back factory: item1, item2</code> → items appear on the Back Factory sheet.<br/>
        · Any mention of the word <code>paint</code> auto-adds a paint checklist to the Back Factory.</td></tr>
      <tr><th>Photo / Drawing Upload</th><td>Optional. Photo of the opening or a hand drawing. Prints on the Office Copy for the installer.</td></tr>
    </table>
  </div>

  <div class="card avoid-break">
    <h3>Action buttons at the bottom of the form</h3>
    <ul>
      <li><strong>Calculate All</strong> — runs every deduction and shows the green Calculated Results box on each blind.</li>
      <li><strong>Reset All</strong> — clears the on-screen form. (Does NOT delete anything from the cloud.)</li>
      <li><strong>Print Worksheet</strong> — opens the full printable Print Preview with all copies.</li>
      <li><strong>Saved Worksheets</strong> — opens the cloud folder of every saved PDF.</li>
      <li><strong>How to Use</strong> — opens this guide.</li>
    </ul>
  </div>

  <h2>4. The Print Preview — what each copy is for</h2>

  <div class="card card-black avoid-break">
    <h3><span class="badge badge-black">Office Copy</span> The Master Record</h3>
    <ul>
      <li>Everything about the job in one place — client details, dimensions, hardware and skin sizes.</li>
      <li>Includes the customer photo/drawing, if uploaded.</li>
      <li>Has an <strong>Admin Checklist</strong> at the bottom: worksheets printed, cover page filled, customer added to Xero, invoicing, check measure, install, post-install.</li>
      <li>Keep in the office folder for the job's paper trail.</li>
    </ul>
  </div>

  <div class="card card-yellow avoid-break">
    <h3><span class="badge badge-yellow">Manufacturing Copy</span> Fabric Team</h3>
    <ul>
      <li>Fabric type &amp; colour, skin cutting sizes (Skin Width, Skin Drop, Pocket Size).</li>
      <li>Manufacturing stock check for consumables (kedda, spline, zips, cotton, pocket fabric).</li>
      <li>Production workflow tick-boxes (skin cut, faults checked, rolled, weighted, etc.).</li>
      <li>Manufacturing Order Form: fabric per blind + total consumables to order.</li>
      <li>For EZY Track it shows <strong>Blind Size only</strong>.</li>
    </ul>
  </div>

  <div class="card card-red avoid-break">
    <h3><span class="badge badge-red">Back Factory Copy</span> Hardware Team</h3>
    <ul>
      <li>Hardware sizes only — Hood, Tube, Bottom Bar, Cassettes, Inner/Outer Channels, Flat Bars.</li>
      <li>Powder coating and paint workflows (only appear if those are selected on the job).</li>
      <li><strong>Cutting Checklist (whole job)</strong> — hoods cut, tubes cut, channels cut, end caps fitted, taken to main factory.</li>
      <li>Back Factory Order Form: totals across the whole job, listed as <strong>whole 6 m lengths needed</strong> — plus components (motors, springs, end caps, dog clips, saddles, remotes, etc.).</li>
      <li>For EZY Track it shows <strong>Hood Size only</strong>.</li>
    </ul>
  </div>

  <div class="card card-blue avoid-break">
    <h3><span class="badge badge-blue">Powder Coating Request Form</span> For Multi-Coat / Lana</h3>
    <ul>
      <li>Only prints if any blind has Powder Coating ticked.</li>
      <li>Auto-populates hardware &amp; sizes to be coated, and the requested colour.</li>
      <li>Quote / Book In tick-boxes at the top.</li>
    </ul>
  </div>

  <h2>5. Printing &amp; saving — three buttons at the top of Print Preview</h2>
  <ol>
    <li><span class="step-num">1</span><strong>PRINT</strong> — opens your device's print dialog. Choose portrait, no scaling. Layout is designed for 3 blinds per A4 page.</li>
    <li><span class="step-num">2</span><strong>SAVE AS PDF</strong> — downloads the full worksheet as a PDF file to the device you're using. On iPhone it also opens the PDF in a new tab so you can Share / Save to Files.</li>
    <li><span class="step-num">3</span><strong>SAVE TO CLOUD</strong> — uploads the PDF to the shared cloud folder. Everyone in the company sees it, from any device.</li>
  </ol>

  <h2>6. Saved Worksheets — the shared cloud folder</h2>
  <p>Click <strong>Saved Worksheets</strong> on the main screen to open the list. Each row shows:</p>
  <ul>
    <li><strong>Name</strong> — starts as <code>Client — Quote# (Date)</code>. You can rename any time.</li>
    <li><strong>Client / Quote # / Job Date / Blinds count / Saved timestamp / File size.</strong></li>
  </ul>
  <p>For each saved worksheet:</p>
  <ul>
    <li><strong>Download</strong> — opens the PDF in a new tab. From a phone you can Share / Save to Files / Email from there.</li>
    <li><strong>Rename</strong> — give it a friendlier name like <em>"Smith alfresco — Feb quote"</em>.</li>
    <li><strong>Delete</strong> — permanently removes the PDF from the shared folder.</li>
  </ul>

  <h2>7. Tips &amp; gotchas</h2>
  <div class="callout">
    <strong>Print settings:</strong> Portrait, no scaling. The layout is designed to fit 3 blinds per A4 page automatically.
  </div>
  <div class="callout">
    <strong>iPhone / iPad PDF save:</strong> If the direct PDF download doesn't trigger, use <strong>Save to Cloud</strong>, then open the saved worksheet — the phone browser will let you Share / Save to Files.
  </div>
  <div class="callout">
    <strong>Powder coating scope:</strong> Leave the Hardware field blank to coat everything. Type keywords like <code>hood</code>, <code>channel</code>, <code>bottom</code>, <code>cassette</code>, <code>tube</code> to limit it.
  </div>
  <div class="callout">
    <strong>Notes prefixes are your friend:</strong> Anything you type after <code>Manufacturing:</code> or <code>Back factory:</code> in the notes becomes a tick-box on the right sheet — great for "double-check the join", "extra motor here", "check clearance to wall" etc.
  </div>
  <div class="callout">
    <strong>Adding new blind types:</strong> When Alfresco / Auto Sun / Zip Track / Wire Guide come back, they'll be re-added once deductions are supplied.
  </div>

  <div class="footer">HOW TO USE — THE OUTDOOR BLIND CO WORKSHEET APP · INTERNAL USE ONLY</div>
</body>
</html>`;
}

// ==================== PDF GENERATION HELPER ====================
async function generatePDFBlobFromHTML(html, jobDetails) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '0';
  tempDiv.style.width = '210mm';
  document.body.appendChild(tempDiv);

  const filename = `Blind_Worksheet_${(jobDetails.clientName || 'Customer').replace(/\s+/g, '_')}_${jobDetails.quoteNo || Date.now()}.pdf`;

  const opt = {
    margin: [10, 10, 10, 10],
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: window.innerWidth < 768 ? (window.devicePixelRatio || 1) : 2,
      windowWidth: 1920,
      useCORS: true,
      logging: false,
      letterRendering: true,
      scrollX: 0,
      scrollY: 0,
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  };

  try {
    const blob = await html2pdf().set(opt).from(tempDiv).outputPdf('blob');
    return { blob, filename };
  } finally {
    document.body.removeChild(tempDiv);
  }
}

// ==================== MAIN COMPONENT ====================
function App() {
  const [jobDetails, setJobDetails] = useState({
    clientName: "",
    quoteNo: "",
    date: new Date().toISOString().split('T')[0]
  });

  const [blinds, setBlinds] = useState([createNewBlind(1)]);
  const [expandedBlinds, setExpandedBlinds] = useState({ 1: true });
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printHTML, setPrintHTML] = useState("");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showSavedJobs, setShowSavedJobs] = useState(false);

  function createNewBlind(num) {
    return {
      id: Date.now() + Math.random(),
      num,
      name: "",
      type: "ezyroll",
      width: "",
      drop: "",
      notes: "",
      // Worksheet options
      fabric: "Clear",
      visiontexColour: "",
      operation: "Spring",
      operationSide: "Left",
      lockHandles: "None",
      weatherFlaps: false,
      valance: "Not Required",
      backingPlate: false,
      reverseGearbox: false,
      gearboxSpacer: false,
      powderCoating: false,
      powderCoatingColour: "",
      powderCoatingHardware: "",
      hoodColour: "",
      channelColour: "",
      bottomBarColour: "",
      trimColour: "None",
      application: "Recess Fit",
      crankStickSize: "",
      // Photo/Drawing upload (for all blind types)
      uploadedDrawing: null,
      uploadedDrawingPreview: "",
      // EZY TRACK specific options
      ezyTrackCentralLocking: false, // Checkbox for Central Locking System
      // EZY TRACK separate measurements (replace single Width when type === 'ezytrack')
      ezyTrackHoodSize: "",   // drives back-factory hardware deductions
      ezyTrackBlindSize: "",  // drives manufacturing fabric deductions
      // EZY TRACK calculated values
      ezyTrackCassetteBack: 0,
      ezyTrackCassetteFront: 0,
      ezyTrackOuter: 0,
      ezyTrackInner: 0,
      ezyTrackFlatBar01: null,
      ezyTrackFlatBar02: null,
      // Calculated results
      calculated: false,
      skinWidth: 0,
      skinDrop: 0,
      hoodSize: 0,
      tubeDiameter: 0,
      tubeLength: 0,
      bottomBarSize: 0
    };
  }

  const addBlind = useCallback(() => {
    const newNum = blinds.length + 1;
    const newBlind = createNewBlind(newNum);
    setBlinds(prev => [...prev, newBlind]);
    setExpandedBlinds(prev => ({ ...prev, [newBlind.id]: true }));
  }, [blinds.length]);

  const removeBlind = useCallback((id) => {
    setBlinds(prev => prev.filter(b => b.id !== id).map((b, i) => ({ ...b, num: i + 1 })));
  }, []);

  const toggleBlind = useCallback((id) => {
    setExpandedBlinds(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const updateBlind = useCallback((id, field, value) => {
    setBlinds(prev => prev.map(b => {
      if (b.id === id) {
        const updated = { ...b, [field]: value };
        // Auto-calculate when dimensions, type, operation, gearbox spacer, or EZY TRACK options change
        if (['width', 'drop', 'type', 'operation', 'gearboxSpacer', 'ezyTrackCentralLocking', 'ezyTrackHoodSize', 'ezyTrackBlindSize'].includes(field)) {
          return calculateBlind(updated);
        }
        return updated;
      }
      return b;
    }));
  }, []);

  const calculateBlind = (blind) => {
    // EZY TRACK special calculations
    if (blind.type === 'ezytrack') {
      const hoodSizeIn = parseFloat(blind.ezyTrackHoodSize) || 0;   // back-factory width basis
      const blindSizeIn = parseFloat(blind.ezyTrackBlindSize) || 0; // manufacturing width basis
      const drop = parseFloat(blind.drop) || 0;

      if (!hoodSizeIn && !blindSizeIn && !drop) return { ...blind, calculated: false };

      // EZY TRACK cassette deductions from HOOD SIZE (back factory)
      const cassetteFront = hoodSizeIn - 13;  // Front cassette: deduct 13mm
      const cassetteBack = hoodSizeIn - 12;   // Back cassette: deduct 12mm

      // Tube + bottom bar deductions from HOOD SIZE (back factory hardware)
      const tube = hoodSizeIn - 130;          // Tube: deduct 130mm
      const bBar = hoodSizeIn - 130;          // Bottom bar: deduct 130mm

      // Fabric/skin deductions from BLIND SIZE (manufacturing)
      const fabric = blindSizeIn - 70;        // Fabric width

      // Deductions from DROP
      const outer = drop - 130;          // Outer channel: deduct 130mm
      const inner = drop - 148;          // Inner channel: deduct 148mm
      const fabricDrop = drop + 200;

      // Central Locking System - Calculate 2 flat bars based on bottom bar length
      let flatBar01 = null;
      let flatBar02 = null;
      let hasCentralLocking = blind.ezyTrackCentralLocking === true;

      if (hasCentralLocking && bBar > 0) {
        const halfBottomBar = bBar / 2;
        flatBar01 = Math.round(halfBottomBar - 113);  // Flat Bar 1: (B/Bar ÷ 2) - 113mm
        flatBar02 = Math.round(halfBottomBar - 40);   // Flat Bar 2: (B/Bar ÷ 2) - 40mm
      }

      return {
        ...blind,
        calculated: true,
        // For backwards-compatibility we mirror hoodSizeIn into width so older code paths still print sensible values
        width: hoodSizeIn || blind.width,
        skinWidth: Math.max(0, Math.ceil(fabric / 10) * 10),
        skinDrop: Math.ceil(fabricDrop / 10) * 10,
        hoodSize: hoodSizeIn, // Use hood size as the EZY TRACK "hood" reference
        hoodSizeLabel: 'Hood Size',
        tubeDiameter: 78, // EZY TRACK always uses 78mm tube
        tubeLength: tube,
        tubeLengthNote: "",
        tubeDiameterNote: "",
        bottomBarSize: bBar,
        bottomBarLabel: "B/Bar",
        bottomBarDiameter: 0,
        bottomBarNote: "",
        // Store all EZY TRACK calculations
        ezyTrackCassetteBack: cassetteBack,
        ezyTrackCassetteFront: cassetteFront,
        ezyTrackOuter: outer,
        ezyTrackInner: inner,
        ezyTrackFlatBar01: flatBar01,
        ezyTrackFlatBar02: flatBar02,
        ezyTrackCentralLocking: hasCentralLocking
      };
    }

    // EZY CHANNEL special calculations
    if (blind.type === 'ezychannel') {
      const width = parseFloat(blind.width) || 0;
      const drop = parseFloat(blind.drop) || 0;

      if (!width && !drop) return { ...blind, calculated: false };

      // Hood size is SAME as width (no deduction)
      const hoodSize = width;
      
      // Tube Length based on operation type
      let tubeLength;
      let tubeLengthNote = "";
      if (blind.operation === 'Crank') {
        if (blind.gearboxSpacer) {
          tubeLength = width - 76;
          tubeLengthNote = "Crank with Gearbox Spacer (-76mm)";
        } else {
          tubeLength = width - 70;
          tubeLengthNote = "Crank operation (-70mm)";
        }
      } else if (blind.operation === 'Motor' || blind.operation === 'Remote') {
        tubeLength = width - 58;
        tubeLengthNote = "Motorised (-58mm)";
      } else {
        // Default to Spring (use Crank values)
        tubeLength = width - 70;
        tubeLengthNote = "Crank operation (-70mm)";
      }
      
      // Bottom bar - Smallest Width deduction
      const bottomBar = width - 50;
      
      // Tube diameter based on width
      let tubeDiameter;
      let tubeDiameterNote;
      if (width < 4000) {
        tubeDiameter = 63;
        tubeDiameterNote = "Under 4m width";
      } else {
        // For over 4m, 70mm or 76mm based on gearbox spacer
        tubeDiameter = blind.gearboxSpacer ? 76 : 70;
        tubeDiameterNote = "Over 4m width";
      }

      // Fabric deductions (using standard deduction)
      const deduction = DEDUCTIONS[blind.type]?.amount || 70;
      const skinWidth = Math.max(0, Math.ceil((width - deduction) / 10) * 10);
      const skinDrop = Math.ceil((drop + 200) / 10) * 10;

      return {
        ...blind,
        calculated: true,
        skinWidth,
        skinDrop,
        hoodSize,
        tubeDiameter,
        tubeLength,
        tubeLengthNote,
        tubeDiameterNote,
        bottomBarSize: bottomBar,
        bottomBarLabel: "Bottom Bar",
        bottomBarDiameter: 0,
        bottomBarNote: "Smallest Width (-50mm)",
        pocketSize: 210 // Always 210mm for EZY CHANNEL
      };
    }

    // Standard blind calculation
    const width = parseFloat(blind.width) || 0;
    const drop = parseFloat(blind.drop) || 0;

    if (!width && !drop) return { ...blind, calculated: false };

    const deduction = DEDUCTIONS[blind.type]?.amount || 0;

    // EXACT ORIGINAL SKIN SIZE FORMULAS (DO NOT CHANGE)
    const skinWidth = Math.max(0, Math.ceil((width - deduction) / 10) * 10);
    const skinDrop = Math.ceil((drop + 200) / 10) * 10;

    // Calculate other sizes based on blind type and options
    const hoodSize = getHoodSize(width, blind.type);
    const tubeDiameter = getTubeDiameter(width, blind.type);
    const tubeLength = getTubeLength(width, blind.type, blind.operation, blind.gearboxSpacer);
    const bottomBarInfo = getBottomBarInfo(width, blind.type);
    const tubeLengthNote = getTubeLengthNote(blind.operation, blind.gearboxSpacer, blind.reverseGearbox);
    const tubeDiameterNote = width < 4000 ? `Under 4m width` : `Over 4m width`;

    return {
      ...blind,
      calculated: true,
      skinWidth,
      skinDrop,
      hoodSize,
      tubeDiameter,
      tubeLength,
      tubeLengthNote,
      tubeDiameterNote,
      bottomBarSize: bottomBarInfo.size,
      bottomBarLabel: bottomBarInfo.label,
      bottomBarDiameter: bottomBarInfo.diameter,
      bottomBarNote: bottomBarInfo.note,
      pocketSize: blind.type === 'ezyroll' ? 210 : null // 210mm for EZY ROLL
    };
  };

  const calculateAll = () => {
    setBlinds(prev => prev.map(calculateBlind));
  };

  const resetAll = () => {
    setJobDetails({ clientName: "", quoteNo: "", date: new Date().toISOString().split('T')[0] });
    setBlinds([createNewBlind(1)]);
    setExpandedBlinds({ 1: true });
  };

  const printAll = () => {
    calculateAll();
    
    const calculatedBlinds = blinds.filter(b => {
      const width = parseFloat(b.width) || 0;
      const drop = parseFloat(b.drop) || 0;
      const ezyHood = parseFloat(b.ezyTrackHoodSize) || 0;
      const ezyBlind = parseFloat(b.ezyTrackBlindSize) || 0;
      return width || drop || ezyHood || ezyBlind;
    }).map(calculateBlind);

    if (calculatedBlinds.length === 0) {
      alert('Please enter measurements for at least one blind first.');
      return;
    }

    const printContent = generatePrintHTML(jobDetails, calculatedBlinds);
    setPrintHTML(printContent);
    setShowPrintPreview(true);
  };

  const closePrintPreview = () => {
    setShowPrintPreview(false);
    setPrintHTML("");
  };

  const openHowToGuide = () => {
    setPrintHTML(generateHowToGuideHTML());
    setShowPrintPreview(true);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow popups for this site to enable printing.');
      return;
    }
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  const handleSaveAsPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const { blob, filename } = await generatePDFBlobFromHTML(printHTML, jobDetails);
      const url = URL.createObjectURL(blob);

      // Try the standard download link first
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Fallback for iOS Safari where `download` is ignored — open in new tab so the user can Share/Save
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        window.open(url, '_blank');
      }

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsGeneratingPDF(false);
      }, 300);
    } catch (error) {
      console.error('PDF generation failed:', error);
      setIsGeneratingPDF(false);
      alert('Failed to generate PDF. Please try again or use the PRINT button as an alternative.');
    }
  };

  const handleSaveToCloud = async () => {
    setIsGeneratingPDF(true);
    try {
      const { blob, filename } = await generatePDFBlobFromHTML(printHTML, jobDetails);
      const form = new FormData();
      form.append('pdf', blob, filename);
      const name = (jobDetails.clientName || 'Customer').trim()
        + (jobDetails.quoteNo ? ` — ${jobDetails.quoteNo.trim()}` : '')
        + (jobDetails.date ? ` (${jobDetails.date})` : '');
      form.append('name', name);
      form.append('client_name', jobDetails.clientName || '');
      form.append('quote_no', jobDetails.quoteNo || '');
      form.append('worksheet_date', jobDetails.date || '');
      form.append('total_blinds', String(blinds.filter(b => b.calculated).length || blinds.length));

      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/worksheets`, { method: 'POST', body: form });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      setIsGeneratingPDF(false);
      alert('Worksheet saved to the cloud folder. Open "Saved Worksheets" to download or share from any device.');
    } catch (error) {
      console.error('Save to cloud failed:', error);
      setIsGeneratingPDF(false);
      alert('Could not save to the cloud. Check your internet connection and try again.');
    }
  };

  const calculatedCount = blinds.filter(b => b.calculated).length;

  return (
    <div className="obc-wrap">
      {/* Header */}
      <header className="obc-header">
        <div className="obc-logo-pill">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <span className="obc-logo-text">The Outdoor Blind Co</span>
        </div>
        <h1 className="obc-h1">Blind Measurement <span>Worksheet</span></h1>
        <p className="obc-subtitle">Complete worksheet with skin, hood, tube & bottom bar calculations</p>
      </header>

      {/* Job Details Card */}
      <div className="obc-job-card">
        <div className="obc-section-label">Job Details</div>
        <div className="obc-grid-4">
          <div className="obc-field obc-span2">
            <label className="obc-label">Client Name</label>
            <input
              type="text"
              data-testid="client-name-input"
              value={jobDetails.clientName}
              onChange={(e) => setJobDetails(prev => ({ ...prev, clientName: e.target.value }))}
              placeholder="Enter client name"
            />
          </div>
          <div className="obc-field">
            <label className="obc-label">Quote No.</label>
            <input
              type="text"
              data-testid="quote-no-input"
              value={jobDetails.quoteNo}
              onChange={(e) => setJobDetails(prev => ({ ...prev, quoteNo: e.target.value }))}
              placeholder="e.g. Q-0042"
            />
          </div>
          <div className="obc-field">
            <label className="obc-label">Date</label>
            <input
              type="date"
              data-testid="date-input"
              value={jobDetails.date}
              onChange={(e) => setJobDetails(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* Blinds List */}
      <div className="obc-blinds-list" data-testid="blinds-list">
        {blinds.map((blind) => (
          <BlindCard
            key={blind.id}
            blind={blind}
            expanded={expandedBlinds[blind.id]}
            onToggle={() => toggleBlind(blind.id)}
            onRemove={() => removeBlind(blind.id)}
            onUpdate={(field, value) => updateBlind(blind.id, field, value)}
            canRemove={blinds.length > 1}
          />
        ))}
      </div>

      {/* Add Blind Button */}
      <button className="obc-add-btn" data-testid="add-blind-btn" onClick={addBlind}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
        Add Blind
      </button>

      {/* Summary Bar */}
      {calculatedCount > 0 && (
        <div className="obc-summary-bar visible" data-testid="summary-bar">
          <div>
            <div className="obc-summary-text">Total blinds calculated</div>
            <div className="obc-summary-count" data-testid="summary-count">
              {calculatedCount} {calculatedCount === 1 ? 'Blind' : 'Blinds'}
            </div>
          </div>
          <div style={{ fontSize: '13px', opacity: 0.65 }}>All results ready to print on worksheet</div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="obc-btn-row">
        <button className="obc-btn obc-btn-calc" data-testid="calculate-all-btn" onClick={calculateAll}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect x="4" y="2" width="16" height="20" rx="2"/>
            <line x1="8" y1="10" x2="16" y2="10"/>
            <line x1="8" y1="14" x2="16" y2="14"/>
            <line x1="8" y1="18" x2="12" y2="18"/>
          </svg>
          Calculate All
        </button>
        <button className="obc-btn obc-btn-reset" data-testid="reset-all-btn" onClick={resetAll}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="1 4 1 10 7 10"/>
            <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
          </svg>
          Reset All
        </button>
        <button className="obc-btn obc-btn-print" data-testid="print-all-btn" onClick={printAll}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 6 2 18 2 18 9"/>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Print Worksheet
        </button>
        <button className="obc-btn obc-btn-saved" data-testid="open-saved-btn" onClick={() => setShowSavedJobs(true)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          Saved Worksheets
        </button>
        <button className="obc-btn obc-btn-help" data-testid="how-to-btn" onClick={openHowToGuide}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          How to Use
        </button>
      </div>

      {/* Footer */}
      <footer className="obc-footer">
        THE OUTDOOR BLIND CO · MEASUREMENT WORKSHEET · INTERNAL USE ONLY
      </footer>

      {/* Print Preview Modal */}
      {showPrintPreview && (
        <div className="print-preview-overlay" data-testid="print-preview">
          <div className="print-preview-header">
            <h2>Print Preview</h2>
            <div className="print-preview-buttons">
              <button className="print-btn-action" onClick={handlePrint} data-testid="print-btn" disabled={isGeneratingPDF}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                PRINT
              </button>
              <button className="print-btn-action" onClick={handleSaveAsPDF} disabled={isGeneratingPDF} style={{ marginLeft: '10px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {isGeneratingPDF ? 'WORKING…' : 'SAVE AS PDF'}
              </button>
              <button className="print-btn-action print-btn-cloud" onClick={handleSaveToCloud} disabled={isGeneratingPDF} style={{ marginLeft: '10px' }} data-testid="save-cloud-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
                  <polyline points="12 12 12 18"/>
                  <polyline points="9 15 12 12 15 15"/>
                </svg>
                {isGeneratingPDF ? 'SAVING…' : 'SAVE TO CLOUD'}
              </button>
              <button className="close-btn-action" onClick={closePrintPreview} data-testid="close-preview-btn" disabled={isGeneratingPDF}>
                ✕ Close
              </button>
            </div>
          </div>
          <iframe
            id="print-iframe"
            srcDoc={printHTML}
            title="Print Preview"
            className="print-preview-iframe"
          />
        </div>
      )}

      {showSavedJobs && (
        <SavedWorksheetsModal onClose={() => setShowSavedJobs(false)} />
      )}
    </div>
  );
}

// ==================== BLIND CARD COMPONENT ====================
function BlindCard({ blind, expanded, onToggle, onRemove, onUpdate, canRemove }) {
  return (
    <div className={`obc-blind-card ${!expanded ? 'collapsed' : ''}`} data-testid={`blind-card-${blind.num}`}>
      {/* Header */}
      <div className="obc-blind-header" onClick={onToggle}>
        <div className="obc-blind-header-left">
          <div className="obc-blind-num">Blind {blind.num}</div>
          <div className="obc-blind-name-display">{blind.name || 'New blind'}</div>
        </div>
        <div className="obc-blind-header-right">
          {blind.calculated && (
            <div className="obc-blind-result-pill visible">
              W: {blind.skinWidth}mm · D: {blind.skinDrop}mm
            </div>
          )}
          {canRemove && (
            <button
              className="obc-btn-remove"
              data-testid={`remove-blind-${blind.num}`}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
            >
              ✕ Remove
            </button>
          )}
          <div className="obc-chevron">⌄</div>
        </div>
      </div>

      {/* Body */}
      <div className="obc-blind-body">
        {/* Dimensions Section */}
        <div className="obc-section-label">Measurements</div>
        <div className="obc-meas-grid-simple">
          <div className="obc-field">
            <label className="obc-label">Blind Name / Location</label>
            <input
              type="text"
              data-testid={`blind-name-${blind.num}`}
              value={blind.name}
              onChange={(e) => onUpdate('name', e.target.value)}
              placeholder="e.g. Living Room"
            />
          </div>
          <div className="obc-field">
            <label className="obc-label">Blind Type</label>
            <select
              data-testid={`blind-type-${blind.num}`}
              value={blind.type}
              onChange={(e) => onUpdate('type', e.target.value)}
            >
              {Object.entries(BLIND_TYPES).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          {blind.type === 'ezytrack' ? (
            <>
              <div className="obc-field">
                <label className="obc-label">Hood Size (mm)</label>
                <input
                  type="number"
                  data-testid={`hood-size-${blind.num}`}
                  value={blind.ezyTrackHoodSize}
                  onChange={(e) => onUpdate('ezyTrackHoodSize', e.target.value)}
                  placeholder="2400"
                />
                <span className="obc-input-hint">Drives Back Factory hardware</span>
              </div>
              <div className="obc-field">
                <label className="obc-label">Blind Size (mm)</label>
                <input
                  type="number"
                  data-testid={`blind-size-${blind.num}`}
                  value={blind.ezyTrackBlindSize}
                  onChange={(e) => onUpdate('ezyTrackBlindSize', e.target.value)}
                  placeholder="2400"
                />
                <span className="obc-input-hint">Drives Manufacturing skin</span>
              </div>
            </>
          ) : (
            <div className="obc-field">
              <label className="obc-label">Width (mm)</label>
              <input
                type="number"
                data-testid={`width-${blind.num}`}
                value={blind.width}
                onChange={(e) => onUpdate('width', e.target.value)}
                placeholder="2400"
              />
            </div>
          )}
          <div className="obc-field">
            <label className="obc-label">Drop (mm)</label>
            <input
              type="number"
              data-testid={`drop-${blind.num}`}
              value={blind.drop}
              onChange={(e) => onUpdate('drop', e.target.value)}
              placeholder="2200"
            />
          </div>
        </div>

        {/* Worksheet Options Section */}
        <div className="obc-section-label" style={{ marginTop: '20px' }}>Worksheet Options</div>
        <div className="obc-options-grid">
          {/* Row 1 */}
          <div className="obc-field">
            <label className="obc-label">Fabric</label>
            <div className="obc-fabric-row">
              <select
                data-testid={`fabric-${blind.num}`}
                value={blind.fabric}
                onChange={(e) => onUpdate('fabric', e.target.value)}
              >
                {FABRIC_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {(blind.fabric === 'Visiontex Plus' || blind.fabric === 'Visiontex Ultra') && (
                <select
                  data-testid={`visiontex-colour-${blind.num}`}
                  value={blind.visiontexColour}
                  onChange={(e) => onUpdate('visiontexColour', e.target.value)}
                  className="obc-visiontex-input"
                >
                  <option value="">Select colour…</option>
                  {(blind.fabric === 'Visiontex Plus' ? VISIONTEX_PLUS_COLOURS : VISIONTEX_ULTRA_COLOURS)
                    .map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
          </div>
          <div className="obc-field">
            <label className="obc-label">Operation</label>
            <select
              data-testid={`operation-${blind.num}`}
              value={blind.operation}
              onChange={(e) => onUpdate('operation', e.target.value)}
            >
              {OPERATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="obc-field">
            <label className="obc-label">Operation Side</label>
            <select
              data-testid={`operation-side-${blind.num}`}
              value={blind.operationSide}
              onChange={(e) => onUpdate('operationSide', e.target.value)}
            >
              {OPERATION_SIDE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="obc-field">
            <label className="obc-label">Lock Handles</label>
            <select
              data-testid={`lock-handles-${blind.num}`}
              value={blind.lockHandles}
              onChange={(e) => onUpdate('lockHandles', e.target.value)}
            >
              {LOCK_HANDLES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>

          {/* Row 2 */}
          <div className="obc-field">
            <label className="obc-label">Valance</label>
            <select
              data-testid={`valance-${blind.num}`}
              value={blind.valance}
              onChange={(e) => onUpdate('valance', e.target.value)}
            >
              {VALANCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="obc-field">
            <label className="obc-label">Application</label>
            <select
              data-testid={`application-${blind.num}`}
              value={blind.application}
              onChange={(e) => onUpdate('application', e.target.value)}
            >
              {APPLICATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="obc-field">
            <label className="obc-label">Trim Colour</label>
            <select
              data-testid={`trim-colour-${blind.num}`}
              value={blind.trimColour}
              onChange={(e) => onUpdate('trimColour', e.target.value)}
            >
              {TRIM_COLOUR_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="obc-field">
            <label className="obc-label">Crank/Pull Stick Size</label>
            <input
              type="text"
              data-testid={`crank-stick-${blind.num}`}
              value={blind.crankStickSize}
              onChange={(e) => onUpdate('crankStickSize', e.target.value)}
              placeholder="e.g. 1200mm"
            />
          </div>

          {/* Row 3 - Colours */}
          <div className="obc-field">
            <label className="obc-label">Hood Colour</label>
            <input
              type="text"
              data-testid={`hood-colour-${blind.num}`}
              value={blind.hoodColour}
              onChange={(e) => onUpdate('hoodColour', e.target.value)}
              placeholder="Enter colour"
            />
          </div>
          <div className="obc-field">
            <label className="obc-label">Channel Colour</label>
            <input
              type="text"
              data-testid={`channel-colour-${blind.num}`}
              value={blind.channelColour}
              onChange={(e) => onUpdate('channelColour', e.target.value)}
              placeholder="Enter colour"
            />
          </div>
          <div className="obc-field">
            <label className="obc-label">Bottom Bar Colour</label>
            <input
              type="text"
              data-testid={`bottom-bar-colour-${blind.num}`}
              value={blind.bottomBarColour}
              onChange={(e) => onUpdate('bottomBarColour', e.target.value)}
              placeholder="Enter colour"
            />
          </div>
          <div className="obc-field">
            <label className="obc-label">Notes</label>
            <input
              type="text"
              data-testid={`notes-${blind.num}`}
              value={blind.notes}
              onChange={(e) => onUpdate('notes', e.target.value)}
              placeholder="Optional notes"
            />
          </div>
        </div>

        {/* Photo/Drawing Upload */}
        <div className="obc-section-label" style={{ marginTop: '20px' }}>Photo/Drawing Upload</div>
        <div className="obc-options-grid">
          <div className="obc-field">
            <label className="obc-label">Upload Drawing or Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  onUpdate('uploadedDrawing', file);
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    onUpdate('uploadedDrawingPreview', reader.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              style={{ fontSize: '14px' }}
            />
          </div>
          {blind.uploadedDrawingPreview && (
            <div className="obc-field">
              <img 
                src={blind.uploadedDrawingPreview} 
                alt="Uploaded drawing" 
                style={{ maxWidth: '200px', maxHeight: '150px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          )}
        </div>

        {/* EZY TRACK Specific Options */}
        {blind.type === 'ezytrack' && (
          <>
            <div className="obc-section-label" style={{ marginTop: '20px' }}>EZY TRACK Options</div>
            <div className="obc-checkbox-row">
              <label className="obc-checkbox-label">
                <input
                  type="checkbox"
                  checked={blind.ezyTrackCentralLocking || false}
                  onChange={(e) => onUpdate('ezyTrackCentralLocking', e.target.checked)}
                />
                Central Locking System (2 Flat Bars)
              </label>
            </div>
          </>
        )}

        {/* Checkboxes Row */}
        <div className="obc-checkbox-row">
          <label className="obc-checkbox-label">
            <input
              type="checkbox"
              data-testid={`weather-flaps-${blind.num}`}
              checked={blind.weatherFlaps}
              onChange={(e) => onUpdate('weatherFlaps', e.target.checked)}
            />
            <span>Weather Flaps</span>
          </label>
          <label className="obc-checkbox-label">
            <input
              type="checkbox"
              data-testid={`backing-plate-${blind.num}`}
              checked={blind.backingPlate}
              onChange={(e) => onUpdate('backingPlate', e.target.checked)}
            />
            <span>Backing Plate</span>
          </label>
          <label className="obc-checkbox-label">
            <input
              type="checkbox"
              data-testid={`reverse-gearbox-${blind.num}`}
              checked={blind.reverseGearbox}
              onChange={(e) => onUpdate('reverseGearbox', e.target.checked)}
            />
            <span>Reverse Gearbox</span>
          </label>
          <label className="obc-checkbox-label">
            <input
              type="checkbox"
              data-testid={`gearbox-spacer-${blind.num}`}
              checked={blind.gearboxSpacer}
              onChange={(e) => onUpdate('gearboxSpacer', e.target.checked)}
            />
            <span>Gearbox Spacer</span>
          </label>
          <label className="obc-checkbox-label">
            <input
              type="checkbox"
              data-testid={`powder-coating-${blind.num}`}
              checked={blind.powderCoating}
              onChange={(e) => onUpdate('powderCoating', e.target.checked)}
            />
            <span>Powder Coating</span>
          </label>
        </div>

        {/* Powder Coating Details - Conditional */}
        {blind.powderCoating && (
          <div className="obc-options-grid" style={{ marginTop: '12px' }}>
            <div className="obc-field">
              <label className="obc-label">Powder Coating Colour</label>
              <input
                type="text"
                value={blind.powderCoatingColour}
                onChange={(e) => onUpdate('powderCoatingColour', e.target.value)}
                placeholder="Enter colour"
              />
            </div>
            <div className="obc-field">
              <label className="obc-label">Which Hardware</label>
              <input
                type="text"
                value={blind.powderCoatingHardware}
                onChange={(e) => onUpdate('powderCoatingHardware', e.target.value)}
                placeholder="e.g. Hood, channels, bottom bar"
              />
            </div>
          </div>
        )}

        {/* Results Section */}
        {blind.calculated && (
          <div className="obc-results-section" data-testid={`results-${blind.num}`}>
            <div className="obc-section-label" style={{ marginTop: '20px' }}>Calculated Results</div>
            <div className="obc-results-grid">
              <div className="obc-result-item">
                <div className="obc-result-label">Blind Type</div>
                <div className="obc-result-value">{BLIND_TYPES[blind.type]}</div>
              </div>
              <div className="obc-result-item">
                <div className="obc-result-label">Width</div>
                <div className="obc-result-value">{blind.width || '-'} mm</div>
              </div>
              <div className="obc-result-item">
                <div className="obc-result-label">Drop</div>
                <div className="obc-result-value">{blind.drop || '-'} mm</div>
              </div>
              <div className="obc-result-item">
                <div className="obc-result-label">Hood Deduction</div>
                <div className="obc-result-value">{DEDUCTIONS[blind.type]?.label}</div>
              </div>
              <div className="obc-result-item highlight">
                <div className="obc-result-label">✓ Skin Width</div>
                <div className="obc-result-value big">{blind.skinWidth} mm</div>
              </div>
              <div className="obc-result-item highlight">
                <div className="obc-result-label">✓ Skin Drop</div>
                <div className="obc-result-value big">{blind.skinDrop} mm</div>
              </div>
              {/* EZY TRACK shows both cassettes, other types show Hood Size */}
              {blind.type === 'ezytrack' ? (
                <>
                  <div className="obc-result-item highlight">
                    <div className="obc-result-label">✓ Cassette Back</div>
                    <div className="obc-result-value big">{blind.ezyTrackCassetteBack} mm</div>
                  </div>
                  <div className="obc-result-item highlight">
                    <div className="obc-result-label">✓ Cassette Front</div>
                    <div className="obc-result-value big">{blind.ezyTrackCassetteFront} mm</div>
                  </div>
                </>
              ) : (
                <div className="obc-result-item highlight">
                  <div className="obc-result-label">✓ Hood Size</div>
                  <div className="obc-result-value big">{blind.hoodSize} mm</div>
                </div>
              )}
              <div className="obc-result-item highlight">
                <div className="obc-result-label">✓ Tube Diameter</div>
                <div className="obc-result-value big">{blind.tubeDiameter} mm</div>
                {blind.tubeDiameterNote && <div className="obc-result-note">{blind.tubeDiameterNote}</div>}
              </div>
              <div className="obc-result-item highlight">
                <div className="obc-result-label">✓ Tube Length</div>
                <div className="obc-result-value big">{blind.tubeLength} mm</div>
                {blind.tubeLengthNote && <div className="obc-result-note">{blind.tubeLengthNote}</div>}
              </div>
              <div className="obc-result-item highlight">
                <div className="obc-result-label">✓ {blind.bottomBarLabel || 'Bottom Bar'}</div>
                <div className="obc-result-value big">{blind.bottomBarSize} mm</div>
                {blind.bottomBarNote && <div className="obc-result-note">{blind.bottomBarNote}</div>}
              </div>
              {/* EZY TRACK Specific - Inner and Outer Channels (always shown) */}
              {blind.type === 'ezytrack' && (
                <>
                  <div className="obc-result-item highlight">
                    <div className="obc-result-label">✓ Inner Channel</div>
                    <div className="obc-result-value big">{blind.ezyTrackInner} mm</div>
                  </div>
                  <div className="obc-result-item highlight">
                    <div className="obc-result-label">✓ Outer Channel</div>
                    <div className="obc-result-value big">{blind.ezyTrackOuter} mm</div>
                  </div>
                </>
              )}
              {/* EZY TRACK Central Locking - 2 Flat Bars */}
              {blind.type === 'ezytrack' && blind.ezyTrackCentralLocking && blind.ezyTrackFlatBar01 !== null && (
                <>
                  <div className="obc-result-item highlight">
                    <div className="obc-result-label">✓ Flat Bar 1</div>
                    <div className="obc-result-value big">{blind.ezyTrackFlatBar01} mm</div>
                    <div className="obc-result-note">(B/Bar ÷ 2) - 113mm</div>
                  </div>
                  <div className="obc-result-item highlight">
                    <div className="obc-result-label">✓ Flat Bar 2</div>
                    <div className="obc-result-value big">{blind.ezyTrackFlatBar02} mm</div>
                    <div className="obc-result-note">(B/Bar ÷ 2) - 40mm</div>
                  </div>
                </>
              )}
              {/* Pocket Size for EZY CHANNEL and EZY ROLL */}
              {(blind.type === 'ezychannel' || blind.type === 'ezyroll') && blind.pocketSize && (
                <div className="obc-result-item highlight">
                  <div className="obc-result-label">✓ Pocket Size</div>
                  <div className="obc-result-value big">{blind.pocketSize} mm</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== PRINT HTML GENERATOR ====================

// Helper: generate MANUFACTURING stock checklist items (consumables + skin materials)
function getManufacturingCheckItems(blind) {
  const items = [];
  
  // Skin / Fabric
  items.push({ part: `Skin / Fabric (${blind.fabric})`, size: `${blind.skinWidth} x ${blind.skinDrop} mm` });
  
  // Consumables - always check these
  items.push({ part: 'Kedda', size: '' });
  items.push({ part: 'Spline', size: '' });
  items.push({ part: 'Zips', size: '' });
  items.push({ part: 'Cotton', size: '' });
  
  // Pocket fabric (if pocket applies)
  if ((blind.type === 'ezychannel' || blind.type === 'ezyroll') && blind.pocketSize) {
    items.push({ part: 'Fabric for Pocket', size: `${blind.pocketSize} mm` });
  }
  
  // Weather flap fabric (if weather flaps selected)
  if (blind.weatherFlaps) {
    items.push({ part: 'Fabric for Weather Flaps', size: '' });
  }
  
  return items;
}

// Helper: generate paint, and production workflow checklist items for manufacturing
// (Powder coating and paint moved to Back Factory per user request)
function getManufacturingExtraChecklist(blind) {
  let html = '';

  // Production workflow
  html += `
      <tr style="background:#f0f0f0;"><td colspan="4" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Fabric &amp; Skin</td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Has the fabric been checked for faults?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Has the fabric been cut?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Has the fabric been welded?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Has the fabric been sewn?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;

  // Weather flaps (conditional)
  if (blind.weatherFlaps) {
    html += `
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Have the weather flaps been made?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;
  }

  html += `
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Has the blind been cleaned?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;

  // Assembly section
  html += `
      <tr style="background:#f0f0f0;"><td colspan="4" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Assembly</td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:700;color:#333;" colspan="2">Have all parts of the blind been assembled?</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">Hood assembled</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">Skin rolled onto tube</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">End caps put on tube</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">Checked correct side for ${blind.operation === 'Motor' || blind.operation === 'Remote' ? 'motor placement' : 'gearbox'}</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">End caps for bottom bar put in</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;

  // EZY Roll specific
  if (blind.type === 'ezyroll') {
    html += `
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">Dog clips and saddles put with job</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;
  }

  // EZY Track central locking
  if (blind.type === 'ezytrack' && blind.ezyTrackCentralLocking) {
    html += `
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">Central locking assembled</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;
  }

  // Motorized - remote check
  if (blind.operation === 'Motor' || blind.operation === 'Remote') {
    html += `
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;padding-left:20px;" colspan="2">Correct remote put with job</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;
  }

  // Final checks
  html += `
      <tr style="background:#f0f0f0;"><td colspan="4" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Final Checks</td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Job bagged / wrapped ready for installation</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">Job put with the hardware</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:700;color:#333;" colspan="2">All hardware complete and wrapped ready to go</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:700;color:#333;" colspan="2">Blind assembled ready to go</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>
      <tr><td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:700;color:#333;" colspan="2">Reception informed blind is completed and ready to go</td><td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td><td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td></tr>`;

  return html;
}

// Helper: generate BACK FACTORY stock checklist items (hardware + components)
function getBackFactoryCheckItems(blind) {
  const items = [];
  
  if (blind.type === 'ezyroll') {
    // Hardware sizes
    items.push({ part: 'Hood', size: `${blind.hoodSize} mm` });
    items.push({ part: `Tube (${blind.tubeDiameter}mm dia)`, size: `${blind.tubeLength} mm long` });
    items.push({ part: 'Bottom Tube (50mm dia)', size: `${blind.bottomBarSize} mm` });
    // Components
    items.push({ part: 'Hood Ends', size: '' });
    items.push({ part: 'End Caps (Tube)', size: '' });
    items.push({ part: 'Dog Clips', size: '' });
    items.push({ part: 'Saddles', size: '' });
    // Operation components (EZY Roll: Crank or Motor only)
    if (blind.operation === 'Crank') items.push({ part: 'Crank Handle', size: blind.crankStickSize || '' });
    if (blind.operation === 'Motor') items.push({ part: 'Motor', size: '' });
    if (blind.operation === 'Remote') {
      items.push({ part: 'Motor', size: '' });
      items.push({ part: 'Remote', size: '' });
    }
    if (blind.gearboxSpacer) items.push({ part: 'Gearbox', size: '' });
    // Finishing
    if (blind.lockHandles !== 'None') items.push({ part: `Lock Handles (${blind.lockHandles})`, size: '' });
    if (blind.trimColour !== 'None') items.push({ part: `Trim (${blind.trimColour})`, size: '' });
    if (blind.backingPlate) items.push({ part: 'Backing Plate', size: '' });
    if (blind.valance !== 'Not Required') items.push({ part: `Valance (${blind.valance})`, size: '' });
    
  } else if (blind.type === 'ezychannel') {
    // Hardware sizes
    items.push({ part: 'Hood', size: `${blind.hoodSize} mm` });
    items.push({ part: `Tube (${blind.tubeDiameter}mm dia)`, size: `${blind.tubeLength} mm long` });
    items.push({ part: 'Bottom Bar', size: `${blind.bottomBarSize} mm` });
    items.push({ part: 'Channels (Left)', size: '' });
    items.push({ part: 'Channels (Right)', size: '' });
    // Components
    items.push({ part: 'Hood Ends', size: '' });
    items.push({ part: 'End Caps (Tube)', size: '' });
    items.push({ part: 'End Caps (Bottom Bar)', size: '' });
    // Operation components (EZY Channel: Crank or Motor only)
    if (blind.operation === 'Crank') items.push({ part: 'Crank Handle', size: blind.crankStickSize || '' });
    if (blind.operation === 'Motor') items.push({ part: 'Motor', size: '' });
    if (blind.operation === 'Remote') {
      items.push({ part: 'Motor', size: '' });
      items.push({ part: 'Remote', size: '' });
    }
    if (blind.gearboxSpacer) items.push({ part: 'Gearbox', size: '' });
    // Finishing
    if (blind.lockHandles !== 'None') items.push({ part: `Lock Handles (${blind.lockHandles})`, size: '' });
    if (blind.trimColour !== 'None') items.push({ part: `Trim (${blind.trimColour})`, size: '' });
    if (blind.backingPlate) items.push({ part: 'Backing Plate', size: '' });
    if (blind.valance !== 'Not Required') items.push({ part: `Valance (${blind.valance})`, size: '' });
    
  } else if (blind.type === 'ezytrack') {
    // Hardware sizes
    items.push({ part: 'Cassette Front', size: `${blind.ezyTrackCassetteFront} mm` });
    items.push({ part: 'Cassette Back', size: `${blind.ezyTrackCassetteBack} mm` });
    items.push({ part: 'Tube (78mm dia)', size: `${blind.tubeLength} mm long` });
    items.push({ part: 'Bottom Bar', size: `${blind.bottomBarSize} mm` });
    items.push({ part: 'Inner Channel (Left)', size: `${blind.ezyTrackInner} mm` });
    items.push({ part: 'Inner Channel (Right)', size: `${blind.ezyTrackInner} mm` });
    items.push({ part: 'Outer Channel (Left)', size: `${blind.ezyTrackOuter} mm` });
    items.push({ part: 'Outer Channel (Right)', size: `${blind.ezyTrackOuter} mm` });
    // Components
    items.push({ part: 'End Caps (Tube)', size: '' });
    items.push({ part: 'End Caps (Bottom Bar)', size: '' });
    if (blind.ezyTrackCentralLocking) {
      items.push({ part: 'Central Locking Kit', size: '' });
      items.push({ part: 'Flat Bar 1 (Central Lock)', size: `${blind.ezyTrackFlatBar01} mm` });
      items.push({ part: 'Flat Bar 2 (Central Lock)', size: `${blind.ezyTrackFlatBar02} mm` });
    }
    // Operation components (EZY Track: Spring or Motor only)
    if (blind.operation === 'Spring') items.push({ part: 'Spring', size: '' });
    if (blind.operation === 'Motor') items.push({ part: 'Motor', size: '' });
    if (blind.operation === 'Remote') {
      items.push({ part: 'Motor', size: '' });
      items.push({ part: 'Remote', size: '' });
    }
    // Finishing
    if (blind.trimColour !== 'None') items.push({ part: `Trim (${blind.trimColour})`, size: '' });
    if (blind.weatherFlaps) items.push({ part: 'Weather Flaps', size: '' });
  }
  
  return items;
}

// Helper: generate powder coating, paint, and cutting workflow checklist items for Back Factory
function getBackFactoryExtraChecklist(blind) {
  let html = '';

  // Powder coating items
  if (blind.powderCoating) {
    html += `
      <tr style="background:#f0f0f0;"><td colspan="4" style="padding:5px 10px;border:1px solid #c0c0c0;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Powder Coating (${blind.powderCoatingColour || 'Colour TBC'})</td></tr>
      <tr><td style="padding:4px 8px;border:1px solid #c0c0c0;font-size:10px;font-weight:600;" colspan="2">Have we contacted powder coaters?</td><td style="padding:4px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td><td style="padding:4px 8px;border:1px solid #c0c0c0;width:110px;"></td></tr>
      <tr><td style="padding:4px 8px;border:1px solid #c0c0c0;font-size:10px;font-weight:600;" colspan="2">Have we dropped the job with the powder coaters?</td><td style="padding:4px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td><td style="padding:4px 8px;border:1px solid #c0c0c0;width:110px;"></td></tr>
      <tr><td style="padding:4px 8px;border:1px solid #c0c0c0;font-size:10px;font-weight:600;" colspan="2">Have we picked up the job from the powder coaters?</td><td style="padding:4px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td><td style="padding:4px 8px;border:1px solid #c0c0c0;width:110px;"></td></tr>`;
  }

  // Paint detection from notes
  const notes = (blind.notes || '').toLowerCase();
  if (notes.includes('paint') || notes.includes('painting') || notes.includes('painted')) {
    html += `
      <tr style="background:#f0f0f0;"><td colspan="4" style="padding:5px 10px;border:1px solid #c0c0c0;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Painting Required (See Notes)</td></tr>
      <tr><td style="padding:4px 8px;border:1px solid #c0c0c0;font-size:10px;font-weight:600;" colspan="2">Has the paint been bought?</td><td style="padding:4px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td><td style="padding:4px 8px;border:1px solid #c0c0c0;width:110px;"></td></tr>
      <tr><td style="padding:4px 8px;border:1px solid #c0c0c0;font-size:10px;font-weight:600;" colspan="2">Has the painting been done?</td><td style="padding:4px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td><td style="padding:4px 8px;border:1px solid #c0c0c0;width:110px;"></td></tr>`;
  }

  return html;
}

// Helper: generate combined CUTTING checklist for ALL blinds (one set of checkboxes for the whole job)
function getBackFactoryCuttingChecklist(blinds) {
  // Determine which cuts are needed based on the blinds in the job
  const hasHood = blinds.some(b => b.type === 'ezyroll' || b.type === 'ezychannel');
  const hasChannels = blinds.some(b => b.type === 'ezychannel' || b.type === 'ezytrack');
  const hasTubes = blinds.length > 0;
  const hasBottomBar = blinds.length > 0;
  const hasCassette = blinds.some(b => b.type === 'ezytrack');
  const hasFlatBars = blinds.some(b => b.type === 'ezytrack' && b.ezyTrackCentralLocking);

  const rows = [];
  rows.push({ label: 'All hoods cut to size', show: hasHood });
  rows.push({ label: 'All cassettes cut to size', show: hasCassette });
  rows.push({ label: 'All tubes cut to size', show: hasTubes });
  rows.push({ label: 'All bottom bars / bottom tubes cut to size', show: hasBottomBar });
  rows.push({ label: 'All channels cut to size', show: hasChannels });
  rows.push({ label: 'All flat bars cut to size (Central Locking)', show: hasFlatBars });
  rows.push({ label: 'End caps fitted to all components', show: true });
  rows.push({ label: 'All cut hardware taken to main factory', show: true, bold: true });

  let html = `<tr style="background:#f0f0f0;"><td colspan="4" style="padding:5px 10px;border:1px solid #c0c0c0;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Cutting Checklist (Whole Job)</td></tr>`;
  rows.filter(r => r.show).forEach(r => {
    html += `<tr><td style="padding:4px 8px;border:1px solid #c0c0c0;font-size:10px;font-weight:${r.bold ? '700' : '600'};color:${r.bold ? '#222' : 'inherit'};" colspan="2">${r.label}</td><td style="padding:4px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td><td style="padding:4px 8px;border:1px solid #c0c0c0;width:110px;"></td></tr>`;
  });
  return html;
}

// Helper: parse notes for section-specific checklist items
function parseNotesForChecklist(notes, section) {
  if (!notes || !notes.trim()) return [];
  
  const items = [];
  const text = notes.trim();
  
  // Match patterns like "manufacturing:" or "notes for manufacturing:" or "back factory:" etc.
  const mfgPatterns = /(?:manufacturing\s*(?:notes)?\s*:|notes\s+for\s+manufacturing\s*:)/i;
  const bfPatterns = /(?:back\s*factory\s*(?:notes)?\s*:|notes\s+for\s+back\s*factory\s*:|factory\s*(?:notes)?\s*:)/i;
  
  // Split text by the section markers
  const mfgMatch = text.search(mfgPatterns);
  const bfMatch = text.search(bfPatterns);
  
  if (section === 'manufacturing' && mfgMatch !== -1) {
    // Get text after the manufacturing marker
    const afterMarker = text.substring(mfgMatch).replace(mfgPatterns, '').trim();
    // End at the next section marker or end of string
    const nextSection = afterMarker.search(bfPatterns);
    const sectionText = nextSection !== -1 ? afterMarker.substring(0, nextSection).trim() : afterMarker.trim();
    // Split by commas, newlines, or semicolons
    const parts = sectionText.split(/[,;\n]+/).map(s => s.trim()).filter(s => s.length > 0);
    parts.forEach(part => items.push(part));
  }
  
  if (section === 'backfactory' && bfMatch !== -1) {
    const afterMarker = text.substring(bfMatch).replace(bfPatterns, '').trim();
    const nextSection = afterMarker.search(mfgPatterns);
    const sectionText = nextSection !== -1 ? afterMarker.substring(0, nextSection).trim() : afterMarker.trim();
    const parts = sectionText.split(/[,;\n]+/).map(s => s.trim()).filter(s => s.length > 0);
    parts.forEach(part => items.push(part));
  }
  
  return items;
}

// Helper: generate order form rows for all blinds (uses back factory items since that's where ordering happens)
function generateOrderFormRows(blinds) {
  return blinds.map((blind, idx) => {
    const items = getBackFactoryCheckItems(blind);
    const rows = items.map(item => `
      <tr>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;text-align:center;">${idx + 1}</td>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;">${blind.name || `Blind ${idx + 1}`}</td>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;">${BLIND_TYPES[blind.type]}</td>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">${item.part}</td>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;text-align:center;">${item.size}</td>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;text-align:center;width:40px;"></td>
        <td style="padding:6px 10px;border:1px solid #c0c0c0;width:130px;"></td>
      </tr>
    `).join('');
    return rows;
  }).join('');
}

// =========================================================
// AGGREGATED ORDER FORMS (in 6m lengths for hardware)
// =========================================================

// Helper: convert total mm needed into number of 6m (6000mm) lengths required
function lengthsNeeded(totalMm) {
  if (totalMm <= 0) return 0;
  return Math.ceil(totalMm / 6000);
}

// Generate the BACK FACTORY aggregated order form
// Aggregates hood/channel/bottom-bar/tube lengths across all blinds into 6m lengths.
// Also lists discrete components (motors, springs, end caps, etc.) with totals.
function generateBackFactoryOrderForm(blinds) {
  // ---- Aggregate linear hardware (mm) ----
  const linear = {}; // key: "Group Name (extra)" -> total mm

  blinds.forEach(blind => {
    if (blind.type === 'ezyroll') {
      // Hood (full width)
      linear['Hood'] = (linear['Hood'] || 0) + (blind.hoodSize || 0);
      // Tube
      const tubeKey = `Tube (${blind.tubeDiameter}mm dia)`;
      linear[tubeKey] = (linear[tubeKey] || 0) + (blind.tubeLength || 0);
      // Bottom Tube
      linear['Bottom Tube (50mm dia)'] = (linear['Bottom Tube (50mm dia)'] || 0) + (blind.bottomBarSize || 0);
    } else if (blind.type === 'ezychannel') {
      linear['Hood'] = (linear['Hood'] || 0) + (blind.hoodSize || 0);
      const tubeKey = `Tube (${blind.tubeDiameter}mm dia)`;
      linear[tubeKey] = (linear[tubeKey] || 0) + (blind.tubeLength || 0);
      linear['Bottom Bar'] = (linear['Bottom Bar'] || 0) + (blind.bottomBarSize || 0);
      // Two channels per blind (drop length)
      const chDrop = parseFloat(blind.drop) || 0;
      linear['Channel'] = (linear['Channel'] || 0) + (chDrop * 2);
    } else if (blind.type === 'ezytrack') {
      // Cassettes
      linear['Cassette Front'] = (linear['Cassette Front'] || 0) + (blind.ezyTrackCassetteFront || 0);
      linear['Cassette Back'] = (linear['Cassette Back'] || 0) + (blind.ezyTrackCassetteBack || 0);
      linear['Tube (78mm dia)'] = (linear['Tube (78mm dia)'] || 0) + (blind.tubeLength || 0);
      linear['Bottom Bar'] = (linear['Bottom Bar'] || 0) + (blind.bottomBarSize || 0);
      // 2 inner + 2 outer channels per blind
      linear['Inner Channel'] = (linear['Inner Channel'] || 0) + ((blind.ezyTrackInner || 0) * 2);
      linear['Outer Channel'] = (linear['Outer Channel'] || 0) + ((blind.ezyTrackOuter || 0) * 2);
      if (blind.ezyTrackCentralLocking) {
        linear['Flat Bar (Central Lock)'] = (linear['Flat Bar (Central Lock)'] || 0)
          + (blind.ezyTrackFlatBar01 || 0) + (blind.ezyTrackFlatBar02 || 0);
      }
    }
  });

  // ---- Aggregate discrete components (count) ----
  const components = {};
  const inc = (k, n = 1) => { components[k] = (components[k] || 0) + n; };

  blinds.forEach(blind => {
    // End caps + hood ends
    if (blind.type === 'ezyroll') {
      inc('Hood Ends (pair)');
      inc('End Caps - Tube (pair)');
      inc('Dog Clips (pair)');
      inc('Saddles (pair)');
    } else if (blind.type === 'ezychannel') {
      inc('Hood Ends (pair)');
      inc('End Caps - Tube (pair)');
      inc('End Caps - Bottom Bar (pair)');
    } else if (blind.type === 'ezytrack') {
      inc('End Caps - Tube (pair)');
      inc('End Caps - Bottom Bar (pair)');
      if (blind.ezyTrackCentralLocking) inc('Central Locking Kit');
    }

    // Operation
    if (blind.operation === 'Crank') inc(`Crank Handle${blind.crankStickSize ? ` (${blind.crankStickSize})` : ''}`);
    if (blind.operation === 'Motor') inc('Motor');
    if (blind.operation === 'Remote') { inc('Motor'); inc('Remote'); }
    if (blind.operation === 'Spring') inc('Spring');
    if (blind.gearboxSpacer) inc('Gearbox');
    if (blind.reverseGearbox) inc('Reverse Gearbox');

    // Finishing
    if (blind.lockHandles && blind.lockHandles !== 'None') {
      const qty = blind.lockHandles === 'Both Sides' ? 2 : 1;
      inc(`Lock Handle (${blind.lockHandles})`, qty);
    }
    if (blind.trimColour && blind.trimColour !== 'None') inc(`Trim - ${blind.trimColour}`);
    if (blind.backingPlate) inc('Backing Plate');
    if (blind.valance && blind.valance !== 'Not Required') inc(`Valance (${blind.valance})`);
    if (blind.weatherFlaps) inc('Weather Flap fittings');
  });

  // ---- Build linear rows (6m lengths) ----
  const linearRows = Object.entries(linear)
    .filter(([, mm]) => mm > 0)
    .map(([part, mm]) => {
      const lengths = lengthsNeeded(mm);
      return `<tr>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">${part}</td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:10px;text-align:center;color:#555;">${mm.toLocaleString()} mm</td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:13px;font-weight:700;text-align:center;color:#b71c1c;">${lengths} × 6 m</td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;width:130px;"></td>
      </tr>`;
    }).join('');

  // ---- Build component rows ----
  const compRows = Object.entries(components)
    .map(([part, qty]) => `<tr>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">${part}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:13px;font-weight:700;text-align:center;color:#b71c1c;">${qty}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;width:130px;"></td>
    </tr>`).join('');

  return `
    <div style="margin-top:12px;border:2px solid #c62828;border-radius:6px;overflow:hidden;page-break-inside:avoid;">
      <div style="background:#c62828;color:white;padding:7px 12px;font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:2px;">
        BACK FACTORY ORDER FORM — TOTALS NEEDED (6 m LENGTHS)
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <tr style="background:#ffebee;">
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Hardware</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:90px;">Total mm</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:90px;">6 m Lengths</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:50px;">Ord.</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:130px;">Supplier / Notes</th>
        </tr>
        ${linearRows}
        ${compRows ? `<tr style="background:#ffebee;"><td colspan="2" style="padding:4px 10px;border:1px solid #c0c0c0;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#b71c1c;">Components</td><td style="padding:4px 10px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;color:#b71c1c;">Qty</td><td colspan="2" style="border:1px solid #c0c0c0;"></td></tr>${compRows}` : ''}
      </table>
      <div style="padding:5px 12px;background:#f9fafb;font-size:9px;color:#6b7f99;font-style:italic;">
        Channel/hood/tube/bottom-bar are listed as totals across the whole job, rounded up to whole 6 m lengths. Tick "Ord." once placed.
      </div>
    </div>`;
}

// Generate the MANUFACTURING aggregated order form (fabric per blind + consumables count)
function generateManufacturingOrderForm(blinds) {
  // ---- Per-blind fabric rows ----
  const fabricRows = blinds.map((blind, idx) => {
    let fabricLabel = blind.fabric || '-';
    if ((blind.fabric === 'Visiontex Plus' || blind.fabric === 'Visiontex Ultra') && blind.visiontexColour) {
      fabricLabel = `${blind.fabric} — ${blind.visiontexColour}`;
    }
    return `<tr>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;text-align:center;font-weight:700;">${idx + 1}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:10px;">${blind.name || `Blind ${idx + 1}`}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">${fabricLabel}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;text-align:center;">${blind.skinWidth} × ${blind.skinDrop} mm</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;width:130px;"></td>
    </tr>`;
  }).join('');

  // ---- Aggregate consumables (totals across all blinds) ----
  const consumables = {};
  const inc = (k, n = 1) => { consumables[k] = (consumables[k] || 0) + n; };

  blinds.forEach(blind => {
    inc('Kedda');
    inc('Spline');
    inc('Zips');
    inc('Cotton (thread)');
    if ((blind.type === 'ezychannel' || blind.type === 'ezyroll') && blind.pocketSize) {
      inc('Pocket fabric');
    }
    if (blind.weatherFlaps) inc('Weather Flap fabric');
  });

  const consumableRows = Object.entries(consumables).map(([part, qty]) => `<tr>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="3">${part}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:13px;font-weight:700;text-align:center;color:#6d4c00;">${qty}</td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:14px;color:#ccc;">&#9744;</span></td>
      <td style="padding:5px 8px;border:1px solid #c0c0c0;width:130px;"></td>
    </tr>`).join('');

  return `
    <div style="margin-top:12px;border:2px solid #f9a825;border-radius:6px;overflow:hidden;page-break-inside:avoid;">
      <div style="background:#f9a825;color:#222;padding:7px 12px;font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:2px;">
        MANUFACTURING ORDER FORM — FABRIC &amp; CONSUMABLES
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <tr style="background:#fff9c4;">
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:35px;">#</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Location</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Fabric / Colour</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Skin Size</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:50px;">Ord.</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:130px;">Supplier / Notes</th>
        </tr>
        ${fabricRows}
        ${consumableRows ? `<tr style="background:#fff9c4;"><td colspan="3" style="padding:4px 10px;border:1px solid #c0c0c0;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6d4c00;">Consumables (whole job)</td><td style="padding:4px 10px;border:1px solid #c0c0c0;text-align:center;font-size:9px;font-weight:700;color:#6d4c00;">Qty</td><td colspan="2" style="border:1px solid #c0c0c0;"></td></tr>${consumableRows}` : ''}
      </table>
      <div style="padding:5px 12px;background:#f9fafb;font-size:9px;color:#6b7f99;font-style:italic;">
        Fabric is listed per blind (skin sizes already include bias allowance). Consumables are totalled across the whole job.
      </div>
    </div>`;
}

// Generate ONE combined checklist for all blinds per department
function generateCombinedChecklist(blinds, copyType) {
  const headerBg = copyType === 'manufacturing' ? '#f9a825' : '#c62828';
  const headerColor = copyType === 'manufacturing' ? '#222' : '#fff';
  const headerLabel = copyType === 'manufacturing' ? 'MANUFACTURING STOCK CHECK & WORKFLOW' : 'PARTS & COMPONENTS CHECK';
  const footerText = copyType === 'manufacturing'
    ? 'Tick each item in stock. Ensure enough consumables before cutting.'
    : 'Tick each item in stock. Any unticked items must be listed on the ORDER FORM for reception.';

  let allRows = '';

  blinds.forEach((blind, idx) => {
    const items = copyType === 'manufacturing'
      ? getManufacturingCheckItems(blind)
      : getBackFactoryCheckItems(blind);

    // Blind header row
    allRows += `<tr style="background:${copyType === 'manufacturing' ? '#fff9c4' : '#ffcdd2'};"><td colspan="4" style="padding:8px 12px;border:1px solid #c0c0c0;font-size:12px;font-weight:700;letter-spacing:1px;color:#222;">BLIND ${idx + 1}${blind.name ? ` - ${blind.name.toUpperCase()}` : ''} (${BLIND_TYPES[blind.type]})</td></tr>`;

    // Stock items
    items.forEach(item => {
      allRows += `<tr>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">${item.part}</td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;text-align:center;">${item.size}</td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td>
        <td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td>
      </tr>`;
    });

    // Extra rows (powder coating, paint, production workflow for manufacturing; powder coating + paint for back factory)
    if (copyType === 'manufacturing') {
      allRows += getManufacturingExtraChecklist(blind);
    } else if (copyType === 'backfactory') {
      allRows += getBackFactoryExtraChecklist(blind);
    }

    // Notes-based items
    const noteItems = parseNotesForChecklist(blind.notes, copyType);
    if (noteItems.length > 0) {
      allRows += `<tr style="background:#f0f0f0;"><td colspan="4" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">From Notes - Blind ${idx + 1}</td></tr>`;
      noteItems.forEach(item => {
        allRows += `<tr>
          <td style="padding:5px 8px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;" colspan="2">${item}</td>
          <td style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;width:60px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td>
          <td style="padding:5px 8px;border:1px solid #c0c0c0;width:120px;"></td>
        </tr>`;
      });
    }
  });

  // Whole-job cutting checklist (back factory only)
  if (copyType === 'backfactory') {
    allRows += getBackFactoryCuttingChecklist(blinds);
  }

  return `
    <div style="margin-top:15px;border:2px solid ${headerBg};border-radius:6px;overflow:hidden;page-break-inside:avoid;">
      <div style="background:${headerBg};color:${headerColor};padding:8px 12px;font-family:'Bebas Neue',sans-serif;font-size:14px;letter-spacing:2px;">
        ${headerLabel}
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <tr style="background:#e8eef5;">
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Part</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Size / Spec</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:60px;">In Stock</th>
          <th style="padding:5px 8px;border:1px solid #c0c0c0;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;width:120px;">Notes</th>
        </tr>
        ${allRows}
      </table>
      <div style="padding:6px 12px;background:#f9fafb;font-size:9px;color:#6b7f99;font-style:italic;">
        ${footerText}
      </div>
    </div>`;
}

function generatePrintHTML(jobDetails, blinds) {
  // Generate ORIGINAL (Office) copy - All information, also compact for 3-per-page
  const originalRows = blinds.map((blind, idx) => {
    const headSizeRow = blind.type === 'ezytrack'
      ? `<div class="result-item"><span class="result-label">Cassette Front</span><span class="result-value">${blind.ezyTrackCassetteFront} mm</span></div>
         <div class="result-item"><span class="result-label">Cassette Back</span><span class="result-value">${blind.ezyTrackCassetteBack} mm</span></div>`
      : `<div class="result-item"><span class="result-label">Hood Size</span><span class="result-value">${blind.hoodSize} mm</span></div>`;
    const ezyTrackExtra = blind.type === 'ezytrack'
      ? `<div class="result-item"><span class="result-label">Inner Ch.</span><span class="result-value">${blind.ezyTrackInner} mm</span></div>
         <div class="result-item"><span class="result-label">Outer Ch.</span><span class="result-value">${blind.ezyTrackOuter} mm</span></div>${blind.ezyTrackCentralLocking && blind.ezyTrackFlatBar01 !== null ? `<div class="result-item"><span class="result-label">Flat Bar 1</span><span class="result-value">${blind.ezyTrackFlatBar01} mm</span></div><div class="result-item"><span class="result-label">Flat Bar 2</span><span class="result-value">${blind.ezyTrackFlatBar02} mm</span></div>` : ''}`
      : '';
    let fabricLabel = blind.fabric;
    if ((blind.fabric === 'Visiontex Plus' || blind.fabric === 'Visiontex Ultra') && blind.visiontexColour) {
      fabricLabel = `${blind.fabric} — ${blind.visiontexColour}`;
    }
    const dimensionsRow = blind.type === 'ezytrack'
      ? `<tr><td class="label-cell">DIMENSIONS</td><td><strong>Hood:</strong> ${blind.ezyTrackHoodSize || '-'} mm</td><td><strong>Blind:</strong> ${blind.ezyTrackBlindSize || '-'} mm</td><td><strong>D:</strong> ${blind.drop || '-'} mm</td><td colspan="3">Op: ${blind.operation}/${blind.operationSide}</td></tr>`
      : `<tr><td class="label-cell">DIMENSIONS</td><td colspan="2"><strong>W:</strong> ${blind.width || '-'} mm</td><td colspan="2"><strong>D:</strong> ${blind.drop || '-'} mm</td><td colspan="2">Op: ${blind.operation}/${blind.operationSide}</td></tr>`;
    return `
    <div class="blind-worksheet compact" style="page-break-inside: avoid;">
      <div class="ws-header">
        <div class="ws-title">Blind ${idx + 1}${blind.name ? ` - ${blind.name}` : ''}</div>
        <div class="ws-type">${BLIND_TYPES[blind.type]}</div>
        <div class="copy-label">ORIGINAL</div>
      </div>
      <table class="options-table">
        ${dimensionsRow}
        <tr><td class="label-cell">FABRIC</td><td colspan="3">${fabricLabel}</td><td colspan="3">App: ${blind.application}</td></tr>
        ${blind.powderCoating ? `<tr><td class="label-cell">POWDER COAT</td><td colspan="6">${blind.powderCoatingColour || 'TBC'} — ${blind.powderCoatingHardware || 'all hardware'}</td></tr>` : ''}
        ${blind.notes ? `<tr><td class="label-cell">NOTES</td><td colspan="6">${blind.notes}</td></tr>` : ''}
        ${blind.uploadedDrawingPreview ? `<tr><td class="label-cell">DRAWING</td><td colspan="6"><img src="${blind.uploadedDrawingPreview}" style="max-width: 250px; max-height: 140px; display: block;" /></td></tr>` : ''}
      </table>
      <div class="results-box">
        <div class="results-title">ALL CALCULATED SIZES</div>
        <div class="results-grid">
          <div class="result-item"><span class="result-label">Skin Width</span><span class="result-value">${blind.skinWidth} mm</span></div>
          <div class="result-item"><span class="result-label">Skin Drop</span><span class="result-value">${blind.skinDrop} mm</span></div>
          ${headSizeRow}
          <div class="result-item"><span class="result-label">Tube ⌀</span><span class="result-value">${blind.tubeDiameter} mm</span></div>
          <div class="result-item"><span class="result-label">Tube Len</span><span class="result-value">${blind.tubeLength} mm</span></div>
          <div class="result-item"><span class="result-label">${blind.bottomBarLabel || 'Bottom Bar'}</span><span class="result-value">${blind.bottomBarSize} mm</span></div>
          ${(blind.type === 'ezychannel' || blind.type === 'ezyroll') && blind.pocketSize ? `<div class="result-item"><span class="result-label">Pocket</span><span class="result-value">${blind.pocketSize} mm</span></div>` : ''}
          ${ezyTrackExtra}
        </div>
      </div>
    </div>
  `;
  }).join('');

  // Generate MANUFACTURING copy - Skin info and dimensions only
  const manufacturingRows = blinds.map((blind, idx) => `
    <div class="blind-worksheet manufacturing compact" style="page-break-inside: avoid;">
      <div class="ws-header manufacturing-header">
        <div class="ws-title">Blind ${idx + 1}${blind.name ? ` - ${blind.name}` : ''}</div>
        <div class="ws-type">${BLIND_TYPES[blind.type]}</div>
        <div class="copy-label">MANUFACTURING</div>
      </div>
      <table class="options-table">
        <tr><td class="label-cell">FABRIC</td><td class="${blind.fabric === 'Clear' ? 'selected' : ''}">Clear</td><td class="${blind.fabric === 'Tint' ? 'selected' : ''}">Tint</td><td class="${blind.fabric === 'Visiontex Plus' ? 'selected' : ''}">VT Plus${blind.fabric === 'Visiontex Plus' && blind.visiontexColour ? ` — ${blind.visiontexColour}` : ''}</td><td class="${blind.fabric === 'Visiontex Ultra' ? 'selected' : ''}">VT Ultra${blind.fabric === 'Visiontex Ultra' && blind.visiontexColour ? ` — ${blind.visiontexColour}` : ''}</td></tr>
        ${blind.type === 'ezytrack'
          ? `<tr><td class="label-cell">DIMENSIONS</td><td colspan="2"><strong>Blind Size:</strong> ${blind.ezyTrackBlindSize || '-'} mm</td><td colspan="2"><strong>Drop:</strong> ${blind.drop || '-'} mm</td></tr>`
          : `<tr><td class="label-cell">DIMENSIONS</td><td colspan="2"><strong>W:</strong> ${blind.width || '-'} mm</td><td colspan="2"><strong>D:</strong> ${blind.drop || '-'} mm</td></tr>`}
        ${blind.weatherFlaps ? `<tr><td class="label-cell">WEATHER FLAPS</td><td class="selected" colspan="4">Yes</td></tr>` : ''}
        ${blind.notes ? `<tr><td class="label-cell">NOTES</td><td colspan="4">${blind.notes}</td></tr>` : ''}
      </table>
      <div class="results-box manufacturing-results">
        <div class="results-title">SKIN CUTTING SIZES</div>
        <div class="results-grid large-results">
          <div class="result-item large"><span class="result-label">Skin Width</span><span class="result-value">${blind.skinWidth} mm</span></div>
          <div class="result-item large"><span class="result-label">Skin Drop</span><span class="result-value">${blind.skinDrop} mm</span></div>
          ${(blind.type === 'ezychannel' || blind.type === 'ezyroll') && blind.pocketSize ? `<div class="result-item large"><span class="result-label">Pocket Size</span><span class="result-value">${blind.pocketSize} mm</span></div>` : ''}
        </div>
      </div>
    </div>
  `).join('');

  // Generate combined Manufacturing checklist for all blinds
  const combinedMfgChecklist = generateCombinedChecklist(blinds, 'manufacturing');

  // Generate BACK FACTORY copy - Hood, Tube, Bottom Bar only (no skin)
  const backFactoryRows = blinds.map((blind, idx) => `
    <div class="blind-worksheet factory compact" style="page-break-inside: avoid;">
      <div class="ws-header factory-header">
        <div class="ws-title">Blind ${idx + 1}${blind.name ? ` - ${blind.name}` : ''}</div>
        <div class="ws-type">${BLIND_TYPES[blind.type]}</div>
        <div class="copy-label">BACK FACTORY</div>
      </div>
      <table class="options-table">
        ${blind.type === 'ezytrack'
          ? `<tr><td class="label-cell">DIMENSIONS</td><td colspan="3"><strong>Hood Size:</strong> ${blind.ezyTrackHoodSize || '-'} mm</td><td colspan="3"><strong>D:</strong> ${blind.drop || '-'} mm</td></tr>`
          : `<tr><td class="label-cell">DIMENSIONS</td><td colspan="2"><strong>W:</strong> ${blind.width || '-'} mm</td><td colspan="3"><strong>D:</strong> ${blind.drop || '-'} mm</td></tr>`}
        <tr><td class="label-cell">OPERATION</td><td class="${blind.operation === 'Spring' ? 'selected' : ''}">Spring</td><td class="${blind.operation === 'Crank' ? 'selected' : ''}">Crank</td><td class="${blind.operation === 'Motor' ? 'selected' : ''}">Motor</td><td class="${blind.operation === 'Remote' ? 'selected' : ''}">Remote</td><td class="${blind.operationSide === 'Left' ? 'selected' : ''}">L</td><td class="${blind.operationSide === 'Right' ? 'selected' : ''}">R</td></tr>
        <tr><td class="label-cell">LOCK / APPLIC.</td><td colspan="2">Lock: ${blind.lockHandles}</td><td colspan="2">App: ${blind.application}</td><td colspan="2">Stick: ${blind.crankStickSize || '-'}</td></tr>
        <tr><td class="label-cell">COLOURS</td><td colspan="2">Hood: ${blind.hoodColour || '-'}</td><td colspan="2">Channel: ${blind.channelColour || '-'}</td><td colspan="2">B/Bar: ${blind.bottomBarColour || '-'}</td></tr>
        <tr><td class="label-cell">TRIM</td><td colspan="6">${blind.trimColour}${blind.weatherFlaps ? ' · Weather Flaps' : ''}${blind.backingPlate ? ' · Backing Plate' : ''}${blind.gearboxSpacer ? ' · Gearbox Spacer' : ''}${blind.reverseGearbox ? ' · Reverse Gearbox' : ''}</td></tr>
        ${blind.powderCoating ? `<tr><td class="label-cell">POWDER COAT</td><td colspan="6">${blind.powderCoatingColour || 'TBC'} — ${blind.powderCoatingHardware || 'all hardware'}</td></tr>` : ''}
        ${blind.notes ? `<tr><td class="label-cell">NOTES</td><td colspan="6">${blind.notes}</td></tr>` : ''}
      </table>
      <div class="results-box factory-results">
        <div class="results-title">HARDWARE CUTTING SIZES</div>
        <div class="results-grid large-results">
          ${blind.type === 'ezytrack' ? `
            <div class="result-item large"><span class="result-label">Cassette Front</span><span class="result-value">${blind.ezyTrackCassetteFront} mm</span></div>
            <div class="result-item large"><span class="result-label">Cassette Back</span><span class="result-value">${blind.ezyTrackCassetteBack} mm</span></div>
          ` : `<div class="result-item large"><span class="result-label">Hood</span><span class="result-value">${blind.hoodSize} mm</span></div>`}
          <div class="result-item large">
            <span class="result-label">Tube</span>
            <span class="result-value">${blind.tubeLength} mm</span>
            <span class="result-note">${blind.tubeDiameter}mm dia${blind.tubeLengthNote ? ` · ${blind.tubeLengthNote}` : ''}</span>
          </div>
          <div class="result-item large">
            <span class="result-label">${blind.bottomBarLabel || 'Bottom Bar'}</span>
            <span class="result-value">${blind.bottomBarSize} mm</span>
          </div>
          ${blind.type === 'ezytrack' ? `
            <div class="result-item large"><span class="result-label">Inner Ch.</span><span class="result-value">${blind.ezyTrackInner} mm</span></div>
            <div class="result-item large"><span class="result-label">Outer Ch.</span><span class="result-value">${blind.ezyTrackOuter} mm</span></div>
          ` : ''}
          ${blind.type === 'ezytrack' && blind.ezyTrackCentralLocking && blind.ezyTrackFlatBar01 !== null ? `
            <div class="result-item large"><span class="result-label">Flat Bar 1</span><span class="result-value">${blind.ezyTrackFlatBar01} mm</span></div>
            <div class="result-item large"><span class="result-label">Flat Bar 2</span><span class="result-value">${blind.ezyTrackFlatBar02} mm</span></div>
          ` : ''}
        </div>
      </div>
    </div>
  `).join('');

  // Generate combined Back Factory checklist for all blinds
  const combinedBfChecklist = generateCombinedChecklist(blinds, 'backfactory');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Blind Worksheet - ${jobDetails.clientName || 'Customer'}</title>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Bebas+Neue&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', Arial, sans-serif; background: #fff; color: #1a2b45; padding: 10mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #222; padding-bottom: 10px; margin-bottom: 15px; }
    .company-name { font-family: 'Bebas Neue', sans-serif; font-size: 24px; letter-spacing: 3px; color: #222; }
    .doc-title { font-family: 'Bebas Neue', sans-serif; font-size: 14px; letter-spacing: 2px; color: #555; }
    .section-divider { page-break-before: always; border-top: 4px solid #222; padding-top: 15px; margin-top: 20px; margin-bottom: 15px; }
    .section-title { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 3px; margin-bottom: 15px; text-align: center; padding: 10px; border-radius: 8px; border: 2px solid; }
    /* Office/Original - Black */
    .section-title.original { background: #f0f0f0; color: #222; border-color: #222; }
    /* Manufacturing - Yellow */
    .section-title.manufacturing { background: #fff9c4; color: #222; border-color: #f9a825; }
    /* Back Factory - Red */
    .section-title.factory { background: #ffebee; color: #b71c1c; border-color: #c62828; }
    /* Order Form */
    .section-title.order { background: #fce4ec; color: #c62828; border-color: #c62828; }
    .meta-row { display: flex; gap: 20px; margin-bottom: 15px; flex-wrap: wrap; }
    .meta-item { background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; padding: 6px 12px; }
    .meta-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #777; }
    .meta-value { font-size: 13px; font-weight: 700; color: #222; }
    .blind-worksheet { border: 2px solid #333; border-radius: 8px; margin-bottom: 15px; overflow: hidden; }
    /* Office header - Black */
    .ws-header { background: #333; color: white; padding: 10px 15px; display: flex; justify-content: space-between; align-items: center; }
    /* Manufacturing header - Yellow with black text */
    .manufacturing-header { background: #f9a825; color: #222; }
    .manufacturing-header .ws-type { background: rgba(0,0,0,0.1); border-color: rgba(0,0,0,0.2); color: #222; }
    .manufacturing-header .copy-label { background: #222; color: #f9a825; }
    /* Back Factory header - Red */
    .factory-header { background: #c62828; color: white; }
    .factory-header .ws-type { background: rgba(255,255,255,0.2); border-color: rgba(255,255,255,0.3); color: white; }
    .factory-header .copy-label { background: white; color: #c62828; }
    .ws-title { font-family: 'Bebas Neue', sans-serif; font-size: 18px; letter-spacing: 2px; }
    .ws-type { background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.3); border-radius: 15px; padding: 3px 12px; font-size: 11px; font-weight: 700; color: white; }
    .copy-label { background: white; color: #333; border-radius: 5px; padding: 4px 12px; font-size: 12px; font-weight: 700; letter-spacing: 1px; }
    .diagram-container { padding: 15px; background: #fafafa; border-bottom: 1px solid #ddd; }
    .diagram-label { text-align: center; font-size: 11px; font-weight: 600; color: #222; padding: 5px 0; }
    .diagram-box { display: flex; border: 2px solid #333; min-height: 60px; margin: 5px 0; }
    .diagram-left, .diagram-right { writing-mode: vertical-lr; text-orientation: mixed; padding: 10px 5px; font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; background: #f0f0f0; border-right: 1px solid #ddd; }
    .diagram-right { border-right: none; border-left: 1px solid #ddd; transform: rotate(180deg); }
    .diagram-center { flex: 1; display: flex; align-items: center; justify-content: center; font-size: 14px; color: #222; }
    .options-table { width: 100%; border-collapse: collapse; font-size: 10px; }
    .options-table td { border: 1px solid #ccc; padding: 6px 8px; text-align: center; }
    .options-table .label-cell { background: #f0f0f0; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; width: 120px; }
    .options-table .selected { background: #333; color: white; font-weight: 700; }
    /* Manufacturing selected cells - yellow */
    .manufacturing .options-table .selected { background: #f9a825; color: #222; }
    .manufacturing .options-table .label-cell { background: #fff9c4; }
    /* Factory selected cells - red */
    .factory .options-table .selected { background: #c62828; color: white; }
    .factory .options-table .label-cell { background: #ffebee; }
    /* Results boxes - lighter, outline-based */
    .results-box { background: #f5f5f5; padding: 12px 15px; border-top: 3px solid #333; }
    .manufacturing-results { background: #fffde7; border-top-color: #f9a825; }
    .factory-results { background: #ffebee; border-top-color: #c62828; }
    .results-title { font-family: 'Bebas Neue', sans-serif; font-size: 14px; letter-spacing: 2px; color: #222; margin-bottom: 8px; }
    .manufacturing-results .results-title { color: #6d4c00; }
    .factory-results .results-title { color: #b71c1c; }
    .results-grid { display: flex; gap: 15px; flex-wrap: wrap; }
    .large-results { justify-content: center; }
    .result-item { background: white; border: 1px solid #ddd; border-radius: 6px; padding: 6px 12px; display: flex; flex-direction: column; }
    .result-item.large { padding: 12px 20px; min-width: 140px; text-align: center; border-width: 2px; }
    .manufacturing .result-item { border-color: #f9a825; }
    .factory .result-item { border-color: #c62828; }
    .result-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #777; }
    .result-value { font-family: 'Bebas Neue', sans-serif; font-size: 20px; color: #222; letter-spacing: 1px; }
    .result-note { font-size: 8px; color: #999; font-style: italic; margin-top: 4px; display: block; }
    .result-item.large .result-value { font-size: 28px; }
    .result-item.large .result-note { font-size: 9px; }
    .dimensions-summary { margin-top: 10px; padding: 10px; background: white; border: 1px solid #ddd; border-radius: 6px; color: #222; font-size: 12px; text-align: center; }
    .print-footer { margin-top: 15px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9px; color: #aaa; letter-spacing: 1.5px; text-align: center; }

    /* ===== COMPACT (3 per A4 page) styling for Manufacturing & Back Factory rows ===== */
    .blind-worksheet.compact { margin-bottom: 6px; border-width: 1.5px; border-radius: 5px; }
    .blind-worksheet.compact .ws-header { padding: 4px 10px; }
    .blind-worksheet.compact .ws-title { font-size: 13px; letter-spacing: 1.5px; }
    .blind-worksheet.compact .ws-type { font-size: 9px; padding: 1px 7px; border-radius: 10px; }
    .blind-worksheet.compact .copy-label { font-size: 9px; padding: 2px 7px; letter-spacing: 0.5px; border-radius: 3px; }
    .blind-worksheet.compact .options-table { font-size: 9px; }
    .blind-worksheet.compact .options-table td { padding: 3px 6px; }
    .blind-worksheet.compact .options-table .label-cell { width: 95px; font-size: 8.5px; letter-spacing: 0.3px; }
    .blind-worksheet.compact .results-box { padding: 5px 10px; border-top-width: 2px; }
    .blind-worksheet.compact .results-title { font-size: 10px; margin-bottom: 4px; letter-spacing: 1.2px; }
    .blind-worksheet.compact .results-grid { gap: 5px; }
    .blind-worksheet.compact .result-item.large { padding: 4px 8px; min-width: 78px; border-width: 1.5px; border-radius: 4px; }
    .blind-worksheet.compact .result-item.large .result-value { font-size: 16px; letter-spacing: 0.5px; }
    .blind-worksheet.compact .result-item.large .result-note { font-size: 7px; margin-top: 1px; }
    .blind-worksheet.compact .result-label { font-size: 7.5px; letter-spacing: 0.5px; }
    @media print {
      @page { size: A4; margin: 8mm; }
      body { padding: 0; }
      .blind-worksheet { page-break-inside: avoid; }
      .section-divider { page-break-before: always; }
    }
  </style>
</head>
<body>

  <!-- ORIGINAL COPY -->
  <div class="print-header">
    <div>
      <div class="company-name">The Outdoor Blind Co</div>
      <div class="doc-title">Blind Measurement Worksheet</div>
    </div>
    <div style="text-align:right;font-size:11px;color:#6b7f99;">
      ${jobDetails.date ? `<div><strong>Date:</strong> ${jobDetails.date}</div>` : ''}
      ${jobDetails.quoteNo ? `<div><strong>Quote:</strong> ${jobDetails.quoteNo}</div>` : ''}
    </div>
  </div>
  <div class="section-title original">OFFICE COPY - Complete Information</div>
  <div class="meta-row">
    <div class="meta-item"><div class="meta-label">Client</div><div class="meta-value">${jobDetails.clientName || '-'}</div></div>
    ${jobDetails.quoteNo ? `<div class="meta-item"><div class="meta-label">Quote No.</div><div class="meta-value">${jobDetails.quoteNo}</div></div>` : ''}
    ${jobDetails.date ? `<div class="meta-item"><div class="meta-label">Date</div><div class="meta-value">${jobDetails.date}</div></div>` : ''}
    <div class="meta-item"><div class="meta-label">Total Blinds</div><div class="meta-value">${blinds.length}</div></div>
  </div>
  ${originalRows}

  <div style="margin-top:12px;border:2px solid #333;border-radius:6px;overflow:hidden;">
    <div style="background:#333;color:white;padding:8px 12px;font-family:'Bebas Neue',sans-serif;font-size:14px;letter-spacing:2px;">
      ADMIN CHECKLIST
    </div>
    <table style="width:100%;border-collapse:collapse;">
      <tr style="background:#f0f0f0;"><td colspan="2" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Accounts &amp; Invoicing</td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Add customer details to Xero</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Print invoice</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Send receipted 50% deposit to customer</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>

      <tr style="background:#f0f0f0;"><td colspan="2" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Printing &amp; Documents</td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed out worksheets</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed and filled out cover page</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed customer information sheet</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed customer's quote</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed customer's invoice</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed job labels for back factory</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed job labels for manufacturing</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed email communication with customer</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>

      <tr style="background:#f0f0f0;"><td colspan="2" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Worksheets &amp; Ordering</td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Handed worksheets to each department</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">All orders for this job have been placed</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr>
        <td colspan="2" style="padding:0;border:1px solid #c0c0c0;">
          <table style="width:100%;border-collapse:collapse;">
            <tr>
              <td style="padding:6px 12px;border-right:1px solid #c0c0c0;font-size:10px;font-weight:600;width:33%;color:#555;">Date Ordered: <span style="border-bottom:1px solid #aaa;display:inline-block;width:90px;">&nbsp;</span></td>
              <td style="padding:6px 12px;border-right:1px solid #c0c0c0;font-size:10px;font-weight:600;width:33%;color:#555;">Date Arriving: <span style="border-bottom:1px solid #aaa;display:inline-block;width:90px;">&nbsp;</span></td>
              <td style="padding:6px 12px;font-size:10px;font-weight:600;width:34%;color:#555;">On Back Order? <span style="font-size:14px;color:#ccc;">&#9744;</span></td>
            </tr>
          </table>
        </td>
      </tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:10px;color:#555;" colspan="2"><strong>Ordering Notes:</strong><div style="border:1px solid #ddd;min-height:40px;margin-top:4px;border-radius:3px;"></div></td></tr>

      <tr style="background:#f0f0f0;"><td colspan="2" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Check Measure</td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Phoned customer and booked in check measure</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Taken 30% deposit for check measure</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Sent confirmation email for check measure</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Phoned customer the day before check measure</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>

      <tr style="background:#f0f0f0;"><td colspan="2" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Installation</td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Booked in the install</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Taken final install payment</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Sent confirmation email for install</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Phoned customer the day before install</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Printed final invoice, care sheet &amp; warranty info and placed in customer folder for installers to give to customer on install day</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>

      <tr style="background:#f0f0f0;"><td colspan="2" style="padding:6px 12px;border:1px solid #c0c0c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#555;">Post-Installation</td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Phoned customer to confirm they are happy with installation</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
      <tr><td style="padding:7px 12px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">Processed final payment for installation</td><td style="padding:7px 12px;border:1px solid #c0c0c0;text-align:center;width:50px;"><span style="font-size:16px;color:#ccc;">&#9744;</span></td></tr>
    </table>
  </div>

  <div class="print-footer">OFFICE COPY · THE OUTDOOR BLIND CO · INTERNAL USE ONLY</div>

  <!-- MANUFACTURING COPY -->
  <div class="section-divider"></div>
  <div class="section-title manufacturing">MANUFACTURING COPY - Skin Sizes & Dimensions</div>
  <div class="meta-row">
    <div class="meta-item"><div class="meta-label">Client</div><div class="meta-value">${jobDetails.clientName || '-'}</div></div>
    ${jobDetails.quoteNo ? `<div class="meta-item"><div class="meta-label">Quote No.</div><div class="meta-value">${jobDetails.quoteNo}</div></div>` : ''}
    <div class="meta-item"><div class="meta-label">Total Blinds</div><div class="meta-value">${blinds.length}</div></div>
  </div>
  ${manufacturingRows}
  ${combinedMfgChecklist}
  ${generateManufacturingOrderForm(blinds)}
  <div class="print-footer">MANUFACTURING COPY · THE OUTDOOR BLIND CO · INTERNAL USE ONLY</div>

  <!-- BACK FACTORY COPY -->
  <div class="section-divider"></div>
  <div class="section-title factory">BACK FACTORY COPY - Hardware Sizes</div>
  <div class="meta-row">
    <div class="meta-item"><div class="meta-label">Client</div><div class="meta-value">${jobDetails.clientName || '-'}</div></div>
    ${jobDetails.quoteNo ? `<div class="meta-item"><div class="meta-label">Quote No.</div><div class="meta-value">${jobDetails.quoteNo}</div></div>` : ''}
    <div class="meta-item"><div class="meta-label">Total Blinds</div><div class="meta-value">${blinds.length}</div></div>
  </div>
  ${backFactoryRows}
  ${combinedBfChecklist}
  ${generateBackFactoryOrderForm(blinds)}
  <div class="print-footer">BACK FACTORY COPY · THE OUTDOOR BLIND CO · INTERNAL USE ONLY</div>

  ${blinds.some(b => b.powderCoating) ? `
  <!-- POWDER COATING REQUEST FORM -->
  <div class="section-divider"></div>
  <div class="section-title" style="background:#e8eaf6;color:#283593;border-color:#3949ab;">POWDER COATING REQUEST FORM</div>
  <div class="meta-row">
    <div class="meta-item"><div class="meta-label">Client Job</div><div class="meta-value">${jobDetails.clientName || '-'}</div></div>
    ${jobDetails.quoteNo ? `<div class="meta-item"><div class="meta-label">Quote No.</div><div class="meta-value">${jobDetails.quoteNo}</div></div>` : ''}
    ${jobDetails.date ? `<div class="meta-item"><div class="meta-label">Date</div><div class="meta-value">${jobDetails.date}</div></div>` : ''}
  </div>

  <div style="border:2px solid #3949ab;border-radius:8px;overflow:hidden;">
    <div style="background:#e8eaf6;padding:16px 20px;">
      <div style="font-size:14px;color:#222;margin-bottom:12px;">
        <strong>To:</strong> Multi-Coat Powder Coating
      </div>
      <div style="font-size:14px;color:#222;margin-bottom:16px;">
        Hi Lana,
      </div>
      <div style="display:flex;gap:30px;margin-bottom:16px;">
        <div style="font-size:13px;font-weight:600;color:#222;display:flex;align-items:center;gap:8px;">
          <span style="display:inline-block;width:22px;height:22px;border:2px solid #3949ab;border-radius:50%;text-align:center;line-height:20px;font-size:12px;color:#ccc;">&#9675;</span>
          Can we get a <strong style="text-decoration:underline;margin:0 4px;">QUOTE</strong> for the following?
        </div>
        <div style="font-size:13px;font-weight:600;color:#222;display:flex;align-items:center;gap:8px;">
          <span style="display:inline-block;width:22px;height:22px;border:2px solid #3949ab;border-radius:50%;text-align:center;line-height:20px;font-size:12px;color:#ccc;">&#9675;</span>
          Can we <strong style="text-decoration:underline;margin:0 4px;">BOOK IN</strong> the following?
        </div>
      </div>
    </div>

    <table style="width:100%;border-collapse:collapse;">
      <tr style="background:#3949ab;color:white;">
        <th style="padding:8px 10px;border:1px solid #283593;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;width:35px;">Blind #</th>
        <th style="padding:8px 10px;border:1px solid #283593;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;">Location</th>
        <th style="padding:8px 10px;border:1px solid #283593;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;">Item</th>
        <th style="padding:8px 10px;border:1px solid #283593;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;">Size</th>
        <th style="padding:8px 10px;border:1px solid #283593;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;">Colour</th>
      </tr>
      ${blinds.filter(b => b.powderCoating).map((blind, idx) => {
        const pcItems = [];
        const pcColour = blind.powderCoatingColour || 'TBC';
        const pcHardware = (blind.powderCoatingHardware || '').toLowerCase();
        const origIdx = blinds.indexOf(blind);
        const wantsAll = !pcHardware || pcHardware.includes('all');

        if (blind.type === 'ezytrack') {
          // EZY Track has cassettes, tube, bottom bar, channels — NO hood
          if (wantsAll || pcHardware.includes('cassette')) {
            pcItems.push({ item: 'Cassette Front', size: `${blind.ezyTrackCassetteFront} mm` });
            pcItems.push({ item: 'Cassette Back', size: `${blind.ezyTrackCassetteBack} mm` });
          }
          if (wantsAll || pcHardware.includes('tube')) {
            pcItems.push({ item: 'Tube (78mm dia)', size: `${blind.tubeLength} mm` });
          }
          if (wantsAll || pcHardware.includes('bottom') || pcHardware.includes('b/bar') || pcHardware.includes('bbar')) {
            pcItems.push({ item: 'Bottom Bar', size: `${blind.bottomBarSize} mm` });
          }
          if (wantsAll || pcHardware.includes('channel')) {
            pcItems.push({ item: 'Inner Channel (Left)', size: `${blind.ezyTrackInner} mm` });
            pcItems.push({ item: 'Inner Channel (Right)', size: `${blind.ezyTrackInner} mm` });
            pcItems.push({ item: 'Outer Channel (Left)', size: `${blind.ezyTrackOuter} mm` });
            pcItems.push({ item: 'Outer Channel (Right)', size: `${blind.ezyTrackOuter} mm` });
          }
        } else {
          // EZY Roll & EZY Channel — have a Hood
          if (wantsAll || pcHardware.includes('hood')) {
            pcItems.push({ item: 'Hood', size: `${blind.hoodSize} mm` });
          }
          if ((wantsAll || pcHardware.includes('channel')) && blind.type === 'ezychannel') {
            pcItems.push({ item: 'Channel (Left)', size: '' });
            pcItems.push({ item: 'Channel (Right)', size: '' });
          }
          if (wantsAll || pcHardware.includes('bottom') || pcHardware.includes('b/bar') || pcHardware.includes('bbar')) {
            pcItems.push({
              item: blind.type === 'ezyroll' ? 'Bottom Tube (50mm dia)' : 'Bottom Bar',
              size: `${blind.bottomBarSize} mm`
            });
          }
        }

        // Custom hardware text that doesn't match known keywords
        const knownKeywords = ['all', 'hood', 'channel', 'bottom', 'cassette', 'tube', 'b/bar', 'bbar'];
        if (pcHardware && !knownKeywords.some(k => pcHardware.includes(k))) {
          pcItems.push({ item: blind.powderCoatingHardware, size: '' });
        }

        if (pcItems.length === 0) return '';

        return pcItems.map((pi, piIdx) => `
          <tr>
            ${piIdx === 0 ? `<td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:12px;text-align:center;font-weight:700;" rowspan="${pcItems.length}">${origIdx + 1}</td>` : ''}
            ${piIdx === 0 ? `<td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;" rowspan="${pcItems.length}">${blind.name || `Blind ${origIdx + 1}`}<br/><span style="font-size:10px;color:#777;">${BLIND_TYPES[blind.type]}</span></td>` : ''}
            <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;font-weight:600;">${pi.item}</td>
            <td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:11px;text-align:center;">${pi.size}</td>
            ${piIdx === 0 ? `<td style="padding:6px 10px;border:1px solid #c0c0c0;font-size:12px;text-align:center;font-weight:700;color:#283593;" rowspan="${pcItems.length}">${pcColour}</td>` : ''}
          </tr>
        `).join('');
      }).join('')}
    </table>

    <div style="padding:16px 20px;background:#f5f5f5;">
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#777;margin-bottom:6px;">Additional Notes</div>
      <div style="border:1px solid #ccc;min-height:50px;background:white;border-radius:4px;"></div>
    </div>

    <table style="width:100%;border-collapse:collapse;margin-top:0;">
      <tr>
        <td style="padding:12px 20px;border:1px solid #c0c0c0;width:50%;font-size:11px;">
          <div style="font-weight:700;text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#6b7f99;margin-bottom:8px;">Sent By</div>
          <div style="border-bottom:1px solid #ccc;height:24px;"></div>
        </td>
        <td style="padding:12px 20px;border:1px solid #c0c0c0;width:25%;font-size:11px;">
          <div style="font-weight:700;text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#6b7f99;margin-bottom:8px;">Date Sent</div>
          <div style="border-bottom:1px solid #ccc;height:24px;"></div>
        </td>
        <td style="padding:12px 20px;border:1px solid #c0c0c0;width:25%;font-size:11px;">
          <div style="font-weight:700;text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#6b7f99;margin-bottom:8px;">Expected Return</div>
          <div style="border-bottom:1px solid #ccc;height:24px;"></div>
        </td>
      </tr>
    </table>
  </div>

  <div class="print-footer">POWDER COATING REQUEST · THE OUTDOOR BLIND CO · MULTI-COAT POWDER COATING</div>
  ` : ''}

</body>
</html>`;
}

// ==================== SAVED WORKSHEETS MODAL ====================
function SavedWorksheetsModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${backend}/api/worksheets`);
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const data = await res.json();
      setItems(data);
    } catch (e) {
      setError(e.message || 'Failed to load saved worksheets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDownload = (item) => {
    // Opening in a new tab works reliably on iOS Safari and desktop.
    window.open(`${backend}/api/worksheets/${item.id}/pdf`, '_blank');
  };

  const handleRename = async (item) => {
    const newName = window.prompt('Rename worksheet', item.name);
    if (!newName || newName.trim() === item.name) return;
    setBusyId(item.id);
    try {
      const res = await fetch(`${backend}/api/worksheets/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (!res.ok) throw new Error(`Rename failed (${res.status})`);
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    setBusyId(item.id);
    try {
      const res = await fetch(`${backend}/api/worksheets/${item.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="saved-modal-overlay" data-testid="saved-modal">
      <div className="saved-modal">
        <div className="saved-modal-header">
          <h2>Saved Worksheets</h2>
          <div>
            <button className="obc-btn" onClick={load} data-testid="saved-refresh-btn" style={{ marginRight: 8 }}>Refresh</button>
            <button className="obc-btn obc-btn-reset" onClick={onClose} data-testid="saved-close-btn">✕ Close</button>
          </div>
        </div>
        <div className="saved-modal-body">
          {loading && <div className="saved-info">Loading…</div>}
          {error && <div className="saved-error">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="saved-info">
              No saved worksheets yet. Open a job in Print Preview and press <strong>Save to Cloud</strong> to save one here.
            </div>
          )}
          {!loading && items.length > 0 && (
            <table className="saved-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Quote #</th>
                  <th>Job Date</th>
                  <th>Blinds</th>
                  <th>Saved</th>
                  <th>Size</th>
                  <th style={{ width: 240 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} data-testid={`saved-row-${item.id}`}>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.client_name || '—'}</td>
                    <td>{item.quote_no || '—'}</td>
                    <td>{item.worksheet_date || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{item.total_blinds}</td>
                    <td>{formatDate(item.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>{formatSize(item.pdf_size_bytes)}</td>
                    <td>
                      <button className="saved-action-btn download" onClick={() => handleDownload(item)} data-testid={`saved-download-${item.id}`}>Download</button>
                      <button className="saved-action-btn" onClick={() => handleRename(item)} disabled={busyId === item.id} data-testid={`saved-rename-${item.id}`}>Rename</button>
                      <button className="saved-action-btn danger" onClick={() => handleDelete(item)} disabled={busyId === item.id} data-testid={`saved-delete-${item.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
